import _, { isObject } from 'lodash'
import { Map, isImmutable, fromJS } from 'immutable'
import { transformCSSPositionValue } from '../hooks/useWidgetComponent'
import { transformCSSStyle } from '../lib/rtluitl'
import { getDefaultStyles } from './getDefaultStyles'
import { IS_SERVER } from '../constants/ssr'
import { getGridData } from './getGridData'
import {
  BACKGROUND_STYLES_KEYS,
  BORDER_KEYS,
  SPREAD_CONTENT_OMIT,
  SPREAD_CONTENT_PREVIEW_OMIT,
} from './BACKGROUND_STYLES_KEYS'
import { getSizeProp, getStyle, makeDropShadowFilter } from './getStyle'
import { FONT_CONST, ALL_SIZE_KEYS, SIZE_KEYS } from './CardComponent'
import { safeLighten } from '../lib/colors'
import { getSmartFieldStyle } from '../components/WidgetBuilder/UI/FormFieldStyle'
import { DRAWER_MOBILE } from './toUnits'
import { iconClasses } from '../lib/iconClasses'

const SMART_STYLES_KEYS = [
  'SmartVLoginField',
  'SmartVVerfField',
  'SmartVRegField',
  'SmartVProfileField',
  'PowerTableField',
  'PowerTableFilterField',
  'PowerTableGFilterField',
  'SmartVLoginFormField',
  'SchedulerField',
  'KanbanEditFields',
  'SmartVOtpFormField',
]

const CHART_TOP_CLASS = [
  'chart__pie',
  'chart__doughnut',
  'chart__horizontalBar',
  'chart__bar',
  'chart__line',
  'chart__stepLine',
  'chart__bubble',
  'chart__scatter',
  'chart__stacked',
  'chart__stackSide',
  'chart__stackLine',
]

const toLS = (v) => String(v).toLowerCase()

const textAlignToFlex = (align) => {
  switch (align) {
    case 'left':
      return 'flex-start'
    case 'right':
      return 'flex-end'
    case 'center':
      return 'center'
  }
  return ''
}

export const kebabCase = (str) => (_.startsWith(str, '--') ? `--${_.kebabCase(str)}` : _.kebabCase(str))

export const getScrollBarStyles = (v) => {
  let styles = {}
  let width = transformCSSPositionValue(v['--scroll-width'], v['--scroll-widthUnit'] || 'px', true)
  let height = transformCSSPositionValue(v['--scroll-height'], v['--scroll-heightUnit'] || 'px', true)
  if (height && height !== 'unset') {
    styles['--scroll-height'] = height
  }
  if (width && width !== 'unset') {
    styles['--scroll-width'] = width
  }
  if (v['scrollBarColor']) {
    styles['--scroll-bar-color'] = v['scrollBarColor']
  }
  if (v['scrollBarImage']) {
    styles['--scroll-bar-image'] = v['scrollBarImage']
  }
  if (v['thumbColor']) {
    styles['--scroll-thumb-color'] = v['thumbColor']
  }
  if (v['thumbImage']) {
    styles['--scroll-thumb-image'] = v['thumbImage']
  }

  return styles
}

const addStyleClasses = (
  prefix,
  styleProps,
  type,
  isHidden = false,
  stripRoot,
  mode,
  isTheme = false,
  isPreview,
  isRTL
) => {
  const PREVIEW_ENV = isPreview
  let important = IMPORTANT_CLASSES.indexOf(type) !== -1 ? '!important' : ''
  if (['Strip', 'Header', 'Footer'].indexOf(type) !== -1) {
    const areaKeys = _.uniq(_.flatten(styleProps.areas))
    let style = []

    const trfStyle = (v) => (isRTL ? transformCSSStyle(v) : v)

    let rootStyle = trfStyle(_.get(styleProps, 'root', {}))
    if (
      type === 'Strip' &&
      ~~styleProps.rows === 1 &&
      ~~styleProps.columns === 1 &&
      String(rootStyle.minHeight).indexOf('unset') !== -1
    ) {
      rootStyle.minHeight = transformCSSPositionValue(styleProps.row0, styleProps.row0Unit)
    }
    if (String(prefix).indexOf('Footer') !== -1) {
      if (rootStyle.height) {
        rootStyle.minHeight = rootStyle.height
        delete rootStyle.height
      }
    }
    const omitParam =
      type === 'Strip' ? [] : _.get(styleProps, mode, 'isEnableSpread', true) ? ['width', 'maxWidth'] : []
    let rootClassBody = _.map(
      _.omit(getStyle(rootStyle), omitParam),
      (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`
    ).join(';\n')
    if (isHidden) {
      rootClassBody += '\n;display: none !important;'
    }
    rootClassBody += '\n;box-sizing: border-box;'
    let customHeaderCSS = ''
    if (['Header'].indexOf(type) !== -1 && _.get(styleProps, 'isEnableFloat', false)) {
      customHeaderCSS = `.ft--theme--Header--root.ft--theme--Header--floatWrap { display: block; }`
    }
    if (['Header'].indexOf(type) !== -1 && _.get(styleProps, 'isEnableFloat', false)) {
      rootClassBody += `\n;
      position: fixed !important;
      left: 0;
      top: ${type === 'Header' ? '0' : 'unset'};
      bottom: ${type === 'Footer' ? '0' : 'unset'};
      right: 0;
      z-index: 100;
`
    }
    if (['Header'].indexOf(type) !== -1 && !_.get(styleProps, 'isEnableSpread', true)) {
      rootClassBody += `;width: calc(${stripRoot ? stripRoot : '100%'});
      min-width: 100%;
      max-width: calc(${stripRoot ? stripRoot : '100%'} );
      margin: 0 auto;`
    }
    if (['Header'].indexOf(type) !== -1 && _.get(styleProps, 'isEnableSpread', true)) {
      rootClassBody += `;width: 100%;
      min-width: 100%;
      max-width: 100%;
      margin: 0`
    }
    style.push(`.${prefix}--root{${rootClassBody}}${customHeaderCSS}`)

    if (type === 'Header') {
      const heightRoot = _.get(styleProps, ['root'], {})
      const height = getSizeProp(heightRoot, 'height')
      style.push(`.ft--theme--Header--first-strip-offset{
        margin-top: -${height} !important;
      }`)
      if (height && height != 'unset') {
        style.push(`.ft--theme--Header--staticPages{
          height: calc(100% - ${height});
        }`)
      } else if (!isTheme && !height) {
        style.push(`.ft--theme--Header--staticPages{
            height: 100%;
          }`)
      }
    }

    let spreadStyle =
      ['Header'].indexOf(type) === -1
        ? {
            ...getStyle({
              ..._.omit(rootStyle, BACKGROUND_STYLES_KEYS),
              background: 'none',
              backgroundColor: 'transparent',
            }),
            ...(['Header', 'Footer'].indexOf(type) !== -1
              ? PREVIEW_ENV
                ? {
                    width: '100vw',
                    minWidth: '100vw',
                    maxWidth: '100vw',
                    ..._.pick(rootStyle, BACKGROUND_STYLES_KEYS),
                  }
                : {
                    width: '100%',
                    minWidth: 'auto',
                    maxWidth: '100%',
                    ..._.pick(rootStyle, BACKGROUND_STYLES_KEYS),
                  }
              : {}),
          }
        : {
            ...getStyle(_.omit(rootStyle, BACKGROUND_STYLES_KEYS)),
            ...(['Header', 'Footer'].indexOf(type) !== -1
              ? PREVIEW_ENV
                ? {
                    width: '100vw',
                    minWidth: '100vw',
                    maxWidth: '100vw',
                  }
                : {
                    width: '100%',
                    minWidth: 'auto',
                    maxWidth: '100%',
                  }
              : {}),
          }
    if (String(prefix).indexOf('Footer') !== -1) {
      if (spreadStyle.height) {
        spreadStyle.minHeight = spreadStyle.height
        delete spreadStyle.height
      }
    }
    spreadStyle = getStyle({ ...spreadStyle })
    const stripSpreadRootClassBody = _.map(spreadStyle, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`).join(
      ';\n'
    )
    style.push(`.${prefix}--stripSpreadRoot{${stripSpreadRootClassBody}}`)
    let shiftMargin = ''
    if (IS_SERVER) {
      shiftMargin = ' - 2px'
    }
    const stripSpreadRoot = {
      ...getStyle(
        _.pick(rootStyle, [
          ...BACKGROUND_STYLES_KEYS,
          'minHeight',
          'minHeightUnit',
          'borderColor',
          'borderStyle',
          'borderWidth',
        ])
      ),
      width: 'calc(100vw - 4px)',
      minWidth: 'calc(100vw - 4px)',
      maxWidth: 'calc(100vw - 4px)',
      position: 'relative',
      marginLeft: `calc(100% / 2 - 50vw)`,
      marginRight: `calc(100% / 2 - 50vw)`,
      ...(!_.get(styleProps, 'isEnableSpread', true)
        ? { backgroundColor: 'transparent', backgroundImage: 'none' }
        : {}),
    }
    if (String(prefix).indexOf('Footer') !== -1) {
      if (stripSpreadRoot.height) {
        stripSpreadRoot.minHeight = stripSpreadRoot.height
        delete stripSpreadRoot.height
      }
    }
    let spreadRootClassBody = _.map(stripSpreadRoot, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`).join(';\n')
    if (['Header'].indexOf(type) !== -1 && _.get(styleProps, 'isEnableFloat', false)) {
      spreadRootClassBody += `\n;
      position: fixed !important;
      left: 0;
      top: ${type === 'Header' ? '0' : 'unset'};
      bottom: ${type === 'Footer' ? '0' : 'unset'};
      right: 0;
      z-index: 100;
`
    }
    style.push(`.${prefix}--spreadRoot{${spreadRootClassBody}}`)

    const spreadContentClassBody = _.map(
      {
        ...getStyle(_.omit(rootStyle, SPREAD_CONTENT_OMIT)),
        marginLeft: 'var(--strip-content-margin)',
        marginRight: 'var(--strip-content-margin)',
        ...(['Header', 'Footer'].indexOf(type) !== -1
          ? PREVIEW_ENV
            ? {
                width: '100%',
                minWidth: '100%',
                maxWidth: '100%',
                margin: '0 auto',
                height: 'auto',
                minHeight: 'unset',
              }
            : {}
          : {
              width: stripRoot ? stripRoot : '100%',
              minWidth: `100%`,
              maxWidth: `calc(${stripRoot ? stripRoot : '100%'})`,
            }),
      },
      (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`
    ).join(';\n')
    if (rootStyle.minHeight) {
      let containerRoot = _.map(
        {
          ...getStyle(_.pick(rootStyle, ['minHeight', 'minHeightUnit'])),
        },
        (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`
      ).join(';\n')
      style.push(`.${prefix}--stripContainerRoot{${containerRoot}}`)
    }
    style.push(`.${prefix}--spreadContent{${spreadContentClassBody}}`)
    if (['Header'].indexOf(type) !== -1) {
      _.map(styleProps, (v, k) => {
        let localImportant = important
        if (type === 'Header' && ['icon', 'badge', 'linkSelected', 'linkActive'].includes(k)) {
          localImportant = '!important'
        }
        if (_.isObject(v) && k != 'root') {
          let styleObject = getStyle(trfStyle(v))
          if (v.fill && _.startsWith(k, 'iconLink')) {
            styleObject['--ft-icon-fill'] = v.fill
          } else if (v.color && _.startsWith(k, 'iconLink')) {
            styleObject['--ft-icon-fill'] = v.color
          }
          let classBody = _.map(styleObject, (val, key) => {
            return `${kebabCase(key)}:${_.trim(val, ';')}${localImportant}`
          })

          classBody = classBody.join(';\n')
          let className = k
          if (k == 'linkActive' || _.endsWith(className, 'Active')) {
            className = String(className).replace('Active', ':active')
          } else if (_.endsWith(className, 'Hover')) {
            className = String(className).replace('Hover', ':hover')
          }
          if (isHidden) {
            classBody += '\n;display: none !important;'
          } else {
            classBody += '\n;'
          }
          classBody += '\n;box-sizing: border-box;'
          style.push(`.${prefix}--${className}{
                  ${classBody}
              }`)
        }
      })
    }

    let overlayStyle = _.get(styleProps, 'overlay', {})
    if (!_.isEmpty(overlayStyle)) {
      const compiledStyle = getStyle(trfStyle(overlayStyle))
      let classBody = _.map(compiledStyle, (val, key) => {
        return `${kebabCase(key)}:${_.trim(val, ';')}`
      }).join(';')
      let overlayClass = `position: absolute;
left: 0;
right: 0;
top: 0;
bottom: 0;
content: "";
${classBody};
width: auto;
height: auto;`
      if (_.get(styleProps, 'isEnableSpread', true)) {
        style.push(`.${prefix}--spreadRoot:after{${overlayClass}}`)
      } else {
        style.push(`.${prefix}--root:after{${overlayClass}}`)
      }
    }

    let overlayStyleActive = _.get(styleProps, 'overlayActive', {})
    if (!_.isEmpty(overlayStyleActive)) {
      const compiledStyle = getStyle(trfStyle(overlayStyleActive))
      let key = ':active'
      let classBody = _.map(compiledStyle, (val, key) => {
        return `${kebabCase(key)}:${_.trim(val, ';')}`
      }).join(';')
      let overlayClass = `position: absolute;
left: 0;
right: 0;
top: 0;
bottom: 0;
content: "";
${classBody};
`
      if (_.get(styleProps, 'isEnableSpread', true)) {
        style.push(`.${prefix}--spreadRoot${key}:after{${overlayClass}}`)
      } else {
        style.push(`.${prefix}--root${key}:after{${overlayClass}}`)
      }
    }
    let overlayStyleHover = _.get(styleProps, 'overlayHover', {})
    if (!_.isEmpty(overlayStyleHover)) {
      const compiledStyle = getStyle(trfStyle(overlayStyleHover))
      let key = ':hover'
      let classBody = _.map(compiledStyle, (val, key) => {
        return `${kebabCase(key)}:${_.trim(val, ';')}`
      }).join(';')
      let overlayClass = `position: absolute;
left: 0;
right: 0;
top: 0;
bottom: 0;
content: "";
${classBody}`
      if (_.get(styleProps, 'isEnableSpread', true)) {
        style.push(`.${prefix}--spreadRoot${key}:after{${overlayClass}}`)
      } else {
        style.push(`.${prefix}--root${key}:after{${overlayClass}}`)
      }
    }
    if (String(prefix).indexOf('Footer') !== -1) {
      let minH = transformCSSPositionValue(_.get(styleProps, 'root.height'), _.get(styleProps, 'root.heightUnit'))
      if (String(prefix).indexOf('theme') === -1) {
        minH &&
          minH !== 'unset' &&
          style.push(`.${prefix}--root .ft--Footer--stripSpreadRoot{min-height: ${minH} !important;}`)
        minH && style.push(`.${prefix}--root .ft--Footer--spreadContent{min-height: ${minH} !important;}`)
        minH && style.push(`.${prefix}--root.ft--Footer--spreadPlaceholder{min-height: ${minH} !important;}`)
      }
      if (!_.get(styleProps, 'isEnableSpread', true)) {
        if (mode === 'lg') {
          style.push(`.${prefix}--spreadContent {${rootClassBody}}`)
        } else {
          style.push(`.${prefix}--spreadContent {${rootClassBody}}`)
        }
      }
    }
    _.each(areaKeys, (area) => {
      const styleObj = trfStyle(_.get(styleProps, `ft__${area}`, {}))
      const classBody = _.map(getStyle(styleObj), (val, key) => `${kebabCase(key)}:${val}`).join(';\n')
      style.push(`.${prefix}--${area}{${classBody}}`)

      const styleObjHover = trfStyle(_.get(styleProps, `ft__${area}Hover`, {}))
      const classBodyHover = _.map(getStyle(styleObjHover), (val, key) => `${kebabCase(key)}:${val}`).join(';\n')
      style.push(`.${prefix}--${area}:hover{${classBodyHover}}`)

      const styleObjActive = trfStyle(_.get(styleProps, `ft__${area}Active`, {}))
      const classBodyActive = _.map(getStyle(styleObjActive), (val, key) => `${kebabCase(key)}:${val}`).join(';\n')
      style.push(`.${prefix}--${area}:active{${classBodyActive}}`)
    })
    return style.join('\n')
  } else {
    let preClass = ''
    let stylesArr = []
    _.each(styleProps, (v, k) => {
      stylesArr.push({
        k: k,
        v: v,
      })
    })
    stylesArr = _.sortBy(stylesArr, (style) =>
      toLS(style.k).indexOf('selected') !== -1 ||
      toLS(style.k).indexOf('active') !== -1 ||
      toLS(style.k).indexOf('hover') !== -1 ||
      toLS(style.k).indexOf('disabled') !== -1
        ? 1
        : -1
    )
    stylesArr = _.sortBy(stylesArr, (style) =>
      toLS(style.k).indexOf('selected') !== -1 ||
      toLS(style.k).indexOf('active') !== -1 ||
      toLS(style.k).indexOf('disabled') !== -1
        ? 1
        : -1
    )
    stylesArr = _.sortBy(stylesArr, (style) => {
      if (type == 'PowerTable') {
        if (style.k === 'thr') {
          return 1000
        } else if (String(style.k).includes('labelComp')) {
          return 1000
        } else if (String(style.k).includes('hcol')) {
          return 1200
        } else if (String(style.k).includes('filtercol')) {
          return 1600
        } else if (String(style.k).includes('filterthr') || String(style.k).includes('globthr')) {
          return 1400
        } else if (String(style.k).includes('tfc-')) {
          return 1500
        } else if (String(style.k).includes('tfc')) {
          return 1400
        } else if (String(style.k).includes('tf')) {
          return 1600
        } else if (String(style.k).includes('thr')) {
          return 1200
        }
        return -1
      } else if (CHART_TOP_CLASS.includes(type)) {
        return 1200
      } else if (style.k === 'iconHover') {
        return 1200
      } else if (style.k === 'iconSelected') {
        return 1000
      } else if (toLS(style.k).indexOf('icon') !== -1) {
        return 1
      } else if (type == 'Tab') {
        if (style.k == 'root') {
          return -2
        } else {
          return -1
        }
      } else if (toLS(style.k).indexOf('disabled') !== -1) {
        return 2
      } else if (
        toLS(style.k).indexOf('selected') !== -1 ||
        toLS(style.k).indexOf('active') !== -1 ||
        toLS(style.k).indexOf('complete') !== -1 ||
        toLS(style.k).indexOf('disabled') !== -1
      ) {
        return 1
      } else {
        return -1
      }
    })
    let styleElem = _.map(stylesArr, ({ v, k }) => {
      if (toLS(k).indexOf('active') !== -1 || toLS(k).indexOf('hover') !== -1 || toLS(k).indexOf('disabled') !== -1) {
        _.each(ALL_SIZE_KEYS, (key) => {
          _.unset(v, key)
        })
      }

      if (type === 'Typography' && (_.endsWith(k, 'Active') || _.endsWith(k, 'Hover'))) {
        return ''
      }
      let classBody = ''
      if (type !== 'PowerTable') {
        if (k === 'activeTab') {
          important = '!important'
        } else {
          important = IMPORTANT_CLASSES.indexOf(type) !== -1 ? '!important' : ''
        }
        if ((type === 'Accordion' && k === 'expanded') || k === 'itemSelected') {
          important = '!important'
        }
      } else if (type === 'PowerTable' && (String(k).indexOf('thr') !== -1 || k === 'paginationBtn')) {
        important = '!important'
      } else if (type === 'PowerTable' && k === 'td') {
        important = ''
      }
      if (_.endsWith(k, 'Active')) {
        important = '!important'
      }
      if (type === 'Accordion' && k === 'root') {
        important = '!important'
      }
      if (type === 'Stepper' && k === 'labelComp') {
        important = '!important'
      }
      if (type === 'chart' && !String(prefix).includes('chart')) {
        important = '!important'
      } else if (type === 'chart' && String(prefix).includes('chart')) {
        important = ''
      }
      if (k === 'legendCol') {
        important = ''
      }
      if (type === 'PowerTable' && String(k).indexOf('icon') !== -1) {
        important = '!important'
      }
      if (type === 'Window' && k === 'root') {
        important = '!important'
      }
      if (_.isObject(v)) {
        if (
          ((String(k).includes('sortIcon') || k === 'checkbox' || k === 'groupIcon') && type === 'PowerTable') ||
          k === 'addonCheckbox'
        ) {
          v.color = v.fill
        }
        if (type === 'list' && k === 'scroll' && v.width) {
          delete v.width
        }
        if (type === 'CES' && String(k).includes('item')) {
          v.justifyContent = textAlignToFlex(v.textAlign)
        }
        if (type === 'StarRating' && _.startsWith(k, 'icon')) {
          _.unset(v, 'backgroundColor')
        }
        if ((k === 'icon' || k === 'iconActive') && v && typeof v == 'object') {
          v['--ft-icon-fill'] = v.fill
          v['--ft-icon-stroke'] = v.stroke
        }
        if (k === 'checkbox' && ['lookupModal', 'chartModal'].includes(type)) {
          v.color = v.fill + '!important'
        }
        if (isRTL) {
          v = transformCSSStyle(v, type === 'table')
        }
        if (['ExternalLookup', 'FormField', 'Search', 'AddressFinder'].includes(type)) {
          if (!v.height) {
            v.height = 'min-content'
          }
        }
        if (type === 'Search' || type === 'AddressFinder') {
          if (v.height !== 'auto' && v.height !== 'min-content') {
            v.height = 'min-content'
          }
        }
        if (type === 'StarRating' && (k === 'iconSelected' || k === 'iconHover')) {
          important = '!important'
        }
        let styleObject = getStyle(v)
        if (type == 'Page') {
          if (!styleObject.width) {
            styleObject.width = stripRoot
          }
          styleObject.maxWidth = `min(${stripRoot}, 100%)`
          if (type === 'Page' && k !== 'root' && mode !== 'lg') {
            styleObject.width = stripRoot
          }
          let scrollStyles = getScrollBarStyles(v)
          v = _.assign({}, v, scrollStyles)
          let pageID = k === 'root' ? '' : `[data-page="${k}"]`
          preClass += `.ft--theme--Window--root.ft--scroll--content${pageID}{${_.map(
            scrollStyles,
            (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`
          ).join(';\n')}}`
        } else if (prefix == 'ft--theme--Section' || prefix == 'ft--theme--Stack') {
          if (!_.endsWith(k, 'Active') && !_.endsWith(k, 'Hover')) {
            if (!styleObject.width) {
              styleObject.width = '300px'
            }
            if (type == 'Stack') {
              if (!styleObject['height']) {
                styleObject['min-height'] = '300px'
              } else {
                styleObject['min-height'] = styleObject['height']
                delete styleObject['height']
              }
            } else if (!styleObject.height) {
              styleObject.height = '300px'
            }
          }
        }
        if (type === 'Stack') {
          if (styleObject.height) {
            styleObject['min-height'] = styleObject.height
            delete styleObject.height
          }
        }
        if (type === 'Progress' && k === 'root') {
          preClass += `.${prefix}--root .ft--color-primary{ background-color: ${safeLighten(styleObject.color, 0.6)};}`
          preClass += `.${prefix}--root .ft--bar{ background-color: ${styleObject.color};}`
        }
        if (k === 'progress') {
          preClass += `.${prefix}--root .ft--color-primary{ background-color: ${safeLighten(styleObject.color, 0.6)};}`
          preClass += `.${prefix}--root .ft--bar{ background-color: ${styleObject.color};}`
        }
        if (k === 'progressWrap') {
          preClass += `.${prefix}--progressWrap .ft--color-primary{ background-color: ${safeLighten(styleObject.color, 0.6)};}`
          preClass += `.${prefix}--progressWrap .ft--bar{ background-color: ${styleObject.color};}`
        }
        if (type === 'Tab' && k === 'scrollButtonsSvg') {
          preClass += `.${prefix}--scrollButtons svg {${_.map(
            styleObject,
            (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`
          ).join(';\n')}}`
        }
        if (type == 'Typography' && k == 'root') {
          if (styleObject.height !== '' && styleObject.height !== 'auto') {
            styleObject.overflow = 'auto'
          }
        }
        let classBody = _.map(
          styleObject,
          (val, key) =>
            `${kebabCase(key)}:${_.trim(val, ';')}${(k === 'root' && key === 'width') || (type === 'Page' && (key === 'maxWidth' || key === 'width')) || (type === 'PowerTable' && ['width', 'height'].includes(key)) ? '' : important}`
        )

        classBody = classBody.join(';\n')
        let className = k
        if (String(k).indexOf('underline') !== -1) {
          const elStyle = `position: relative;`
          classBody = elStyle
          const elAfterStyle = `
          left: 0;
          right: 0;
          bottom: 0;
          content: "\\00a0";
          position: absolute;
          border-bottom: ${typeof v.borderWidth === 'undefined' ? '1px' : v.borderWidth}px solid ${typeof v.borderColor === 'undefined' ? 'rgba(0, 0, 0, 0.42)' : v.borderColor};
          pointer-events: none;`
          preClass += `.${prefix}--${k}:before {${elAfterStyle}}
          .${prefix}--${k}:after {display: none;}
          .${prefix}--${k}:hover:before{
            border-bottom-color: ${v.activeBorderColor} !important;
            border-bottom-width: ${v.activeBorderWidth}px !important;
          }`
          if (v.visibility === 'hidden') {
            preClass += `.${prefix}--${k}:before, .${prefix}--${k}:after {display: none;} `
          }
        }
        if (_.endsWith(className, 'Hover') && type == 'Range') {
          className = String(className).replace('Hover', ':hover')
        } else if (
          _.endsWith(className, 'Active') &&
          (type == 'YesNo' || type == 'ImageChoice' || type == 'CES' || type == 'SurveyList')
        ) {
          className = String(className).replace('Active', '.active')
        } else if (_.endsWith(className, 'Disabled') && isAllowState(type, k)) {
          className = String(className).replace('Disabled', ':disabled')
        } else if (type == 'Drawer' && k === 'linkActive') {
          className = String(className).replace('Active', '.active')
        } else if (className !== 'iconActive' && _.endsWith(className, 'Active') && isAllowState(type, k)) {
          className = String(className).replace('Active', ':active')
        } else if (type === 'StarRating' && _.endsWith(className, 'Hover') && isAllowState(type, k)) {
          className = String(className).replace('Hover', ':hover') + `,span.${prefix}--${className}`
        } else if (_.endsWith(className, 'Hover') && isAllowState(type, k)) {
          className = String(className).replace('Hover', ':hover') + `,.${prefix}--${className}`
        }
        let postFix = className
        if (type === 'galleryCarousel') {
          if (k === 'carouselIcon') {
            const prf = _.trimStart(String(prefix).split('image-gallery-thumbnail').join(''), '.')
            if (v.arrowIconColor) {
              preClass += `.${prf}--root .image-gallery-left-nav svg, .${prf}--root .image-gallery-right-nav svg{color: ${v.arrowIconColor};}`
            }
            if (v.playIcon) {
              preClass += `.${prf}--root .image-gallery-play-button svg{color: ${v.playIcon}!important;}`
            }
            if (v.fullScreenIcon) {
              preClass += `.${prf}--root .image-gallery-fullscreen-button svg{color: ${v.fullScreenIcon}!important;}`
            }
          }
          if (k === 'original' || k === 'thumbnail' || k === 'imageDescription') {
            if (k === 'imageDescription') {
              postFix = `${postFix} .image-gallery-description`
            } else if (k === 'original') {
              postFix = `${postFix} img.image-gallery-image`
            } else {
              prefix = 'image-gallery-thumbnail.' + prefix
            }
            classBody = _.map(styleObject, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}!important`).join(';\n')
          }
          classBody = _.map(styleObject, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}!important`).join(';\n')
          if (_.endsWith(k, 'Active')) {
            preClass += `.${prefix}--${String(k).replace('Active', '.active')}{${classBody}}`
          }
        }
        if (isHidden) {
          classBody += '\n;display: none !important;'
        } else {
          classBody += '\n;'
        }
        classBody += '\n;box-sizing: border-box;'

        if ((prefix == 'ft--theme--form' || type == 'form') && className == 'label') {
          postFix = `${className}, .${prefix}--${className}.MuiFormLabel-root.Mui-focused`
        } else if ((prefix == 'ft--theme--form' || type == 'form') && className == 'floatLabel') {
          postFix = `label.MuiFormLabel-filled.MuiInputLabel-formControl, .${prefix}--label.MuiFormLabel-root.Mui-focused.MuiInputLabel-formControl`
        }
        if (type === 'form' && k === 'button') {
          let btnAlign = `.${prefix}--button--align{
            justify-content: ${styleObject.justifyContent || 'flex-start'};
          }`
          preClass += btnAlign
        }
        if (type === 'Form' && k === 'root') {
          if (styleObject.gridGap) {
            preClass += `.${prefix}--root .ft--theme--Form--wrapper{ grid-gap: ${styleObject.gridGap};}`
          }
        }
        if (type === 'Typography' && k === 'root') {
          let classBody = _.compact(
            _.map(styleObject, (val, key) => {
              if (FONT_CONST.indexOf(key) !== -1) {
                return `${kebabCase(key)}:${val}${important}`
              }
            })
          ).join(';\n')
          preClass += `.${prefix}--root-to-all * {
            margin: 0;
            ${classBody}
          }`
        }
        if (k === 'itemSelected' && type === 'TreeView' && styleProps['item']?.backgroundColor) {
          preClass += `.${prefix}--${postFix} > .ftgroup{background-color: ${styleProps['item']?.backgroundColor};}`
        }
        if (k === 'itemSelected' && type === 'TreeView') {
          postFix += `.ft--theme--TreeView--item`
        }
        if (type === 'Stack') {
          let colStyle = `.${prefix}--col{
            width: auto;
            min-width: ${transformCSSPositionValue(_.get(v, 'width', 0), _.get(v, 'widthUnit', 'px'), true)}
          }`
          let stackMinHeight = transformCSSPositionValue(_.get(v, 'height', 0), _.get(v, 'heightUnit', 'px'), true)
          preClass += `.${prefix}--row{
            height: auto;
            min-height: ${stackMinHeight}
          }
          .${prefix}--row > .stack-element-separator-row{
            max-width: 100%;
            max-width: ${transformCSSPositionValue(_.get(v, 'width', 0), _.get(v, 'widthUnit', 'px'))};
          }${colStyle}`
        }
        if (k === 'scroll') {
          const scrollBarColor = _.get(styleObject, 'scrollBarColor', 'auto')
          const scrollBarImage = _.get(styleObject, 'scrollBarImage', '')
          const thumbColor = _.get(styleObject, 'thumbColor', scrollBarColor ? 'ButtonFace' : '')
          const thumbImage = _.get(styleObject, 'thumbImage', '')
          const borderRadius = _.get(styleObject, 'borderRadius', '')
          let borderWidth = _.get(styleObject, 'borderWidth', '')
          let borderColor = _.get(styleObject, 'borderColor', '')
          let borderStyle = _.get(styleObject, 'borderStyle', '')
          const isCustomScroll =
            (Boolean(scrollBarColor) || Boolean(scrollBarImage)) && (Boolean(thumbColor) || Boolean(thumbImage))
          classBody = ''
          if (isCustomScroll) {
            // classBody = 'scrollbar-width: thin;';
            // if (scrollBarColor && thumbColor) {
            //     classBody += `scrollbar-color: ${thumbColor} ${scrollBarColor};`;
            // }
            const classes = []
            let height = _.get(styleObject, 'height')
            let width = _.get(styleObject, 'width')
            let heightUnit = _.get(styleObject, 'heightUnit', 'px')
            if (!height) {
              height = _.get(styleObject, '--scroll-height')
              heightUnit = _.get(styleObject, '--scroll-heightUnit', 'px')
            }

            if (height && height !== 'auto' && height !== 'unset') {
              height = _.endsWith(height, 'px') ? String(height).replace('px', '') : height
              height = transformCSSPositionValue(height, heightUnit, true)
              height = `height:${height} !important;`
            } else {
              height = ''
            }
            classes.push(
              `.${prefix}--${postFix}::-webkit-scrollbar{width: ${_.get(styleObject, 'width', '14px')};${height}}`
            )
            // dynamically adds padding-right or padding-left in table head according to the direction, in order to leave room for the scrollbar, can be removed
            if (type === 'PowerTable') {
              classes.push(
                `.${prefix}--thead{padding-${isRTL ? 'left' : 'right'}: ${_.get(styleObject, 'width', '14px')}!important;}`
              )
            }

            classes.push(`.${prefix}--${postFix}::-webkit-scrollbar-track {
              background-color: ${scrollBarColor};
              ${scrollBarImage ? 'background-image:' + scrollBarImage : ''};
              ${Boolean(scrollBarColor) ? 'background-image: none;' : ''}
            }`)
            if (borderWidth || borderStyle || borderColor) {
              if (!borderWidth) {
                borderWidth = '1px'
              }
              if (!borderStyle) {
                borderStyle = 'solid'
              }
              if (!borderColor) {
                borderColor = 'black'
              }
            }
            classes.push(`.${prefix}--${postFix}::-webkit-scrollbar-thumb {
              background-color: ${thumbColor};
              ${thumbImage ? 'background-image:' + thumbImage : ''};
              ${Boolean(thumbColor) ? 'background-image: none;' : ''}
              ${borderRadius ? `border-radius: ${borderRadius};` : ''}
              ${borderWidth && borderColor && borderStyle ? `border: ${borderWidth} ${borderStyle} ${borderColor};` : ''}
            }`)
            let extraRoot = '' //`.ft--apply--to--child--scroll > div {scrollbar-width: thin;`;
            // if (scrollBarColor && thumbColor) {
            //     extraRoot += `scrollbar-color: ${thumbColor} ${scrollBarColor};`;
            // }
            // extraRoot += '}';
            classes.push(
              `.${prefix}--${postFix}.ft--apply--to--child--scroll > div::-webkit-scrollbar{width: ${_.get(styleObject, 'width', '14px')};${height}}`
            )
            classes.push(`.${prefix}--${postFix}.ft--apply--to--child--scroll > div::-webkit-scrollbar-track {
              background-color: ${scrollBarColor};
              ${scrollBarImage ? 'background-image:' + scrollBarImage : ''};
              ${Boolean(scrollBarColor) ? 'background-image: none;' : ''}
            }`)
            classes.push(`.${prefix}--${postFix}.ft--apply--to--child--scroll > div::-webkit-scrollbar-thumb {
              background-color: ${thumbColor};
              ${thumbImage ? 'background-image:' + thumbImage : ''};
              ${Boolean(thumbColor) ? 'background-image: none;' : ''}
              ${borderRadius ? `border-radius: ${borderRadius};` : ''}
              ${borderWidth && borderColor && borderStyle ? `border: ${borderWidth} ${borderStyle} ${borderColor};` : ''}
            }`)
            preClass += extraRoot + classes.join('\n')
          }
        }
        if (String(k).indexOf('overlay') !== -1) {
          let key = String(k).indexOf('Active') !== -1 ? ':active' : String(k).indexOf('Hover') !== -1 ? ':hover' : ''
          let overlayClass = `position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            pointer-events: none;
            min-height: unset !important;
            content: "";
            ${classBody};
            width: auto;
            height: auto;`
          preClass += `.${prefix}--root${key}:after{${overlayClass}}`
          classBody = ''
        }
        if (type === 'UserTip') {
          let color = v.borderColor || v.backgroundColor
          if (k === 'arrow' && v.color) {
            color = v.color
          }
          preClass += `.${prefix}--arrow{color: ${color};box-shadow: ${v.boxShadow};}`
        }

        if (k === 'modal' && type === 'FormField') {
          let key = `.${prefix}--${postFix}`
          _.each(v, (v, k) => {
            let styleObject = getStyle(v)
            let classBody = _.map(styleObject, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}!important`).join(
              ';'
            )
            preClass += `${key}--${k}{${classBody}}`
          })
        }

        if (type === 'eComSlide' && k === 'slide-root') {
          preClass += `.${prefix}--slide-root ~ .ft--nav--slides-ctrl > .category-item-slide{ max-width: ${styleObject.width};}`
        }
        if (type === 'PowerTable' && k === 'root') {
          preClass += `.${prefix}--${postFix}.ft--theme--PowerTable-autogrow{height: auto !important; min-height: auto;max-height: auto;}`
          preClass += `.${prefix}--${postFix} .ft--theme--PowerTable-autogrow-min{height: auto !important;max-height: auto;}`
        }
        if (type === 'list' && k === 'root') {
          preClass += `.${prefix}--${postFix}.ft--theme--list-autogrow{height: auto !important; min-height: auto;max-height: auto;}`
        }

        if (k === 'listAutoComplete') {
          let scrollStyles = getScrollBarStyles(v)
          let str = _.map(scrollStyles, (val, key) => `${key}:${_.trim(val, ';')}`).join(';')
          preClass += `.${prefix}--${k}{${str}}`
        }

        if (k === 'progress') {
          const styleObj = _.pick(styleObject, [
            'marginLeft',
            'marginRight',
            'marginTop',
            'marginBottom',
            'width',
            'widthUnit',
            'minWidth',
            'minWidthUnit',
            'maxWidth',
            'maxWidthUnit',
            'minHeight',
            'height',
            'maxHeight',
            'minHeightUnit',
            'heightUnit',
            'maxHeightUnit',
          ])
          if (styleObj.widthUnit === '%') {
            styleObj.width = '100'
            styleObj.widthUnit = '%'
          }
          let styles = getStyle(styleObj)
          if (_.size(styles) > 0) {
            let str = _.map(styles, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}!important`).join(';')
            preClass += `.${prefix}--root .progress-root{${str};height: auto !important;}`
          }
          preClass += `.${prefix}--root .ft--color-primary{ background-color: ${safeLighten(styleObject.color, 0.6)};${styleObject.lineWidth ? 'height: ' + styleObject.lineWidth + 'px;' : ''}}`
          preClass += `.${prefix}--root .ft--bar{ background-color: ${styleObject.color};}`
        }
        if (k === 'progressWrap') {
          const styleObj = _.pick(styleObject, [
            'width',
            'widthUnit',
            'minWidth',
            'minWidthUnit',
            'maxWidth',
            'maxWidthUnit',
            'minHeight',
            'height',
            'maxHeight',
            'minHeightUnit',
            'heightUnit',
            'maxHeightUnit',
          ])
          if (styleObj.widthUnit === '%') {
            styleObj.width = '100'
            styleObj.widthUnit = '%'
          }
          let styles = getStyle(styleObj)
          if (_.size(styles) > 0) {
            let str = _.map(styles, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}!important`).join(';')
            preClass += `.${prefix}--progressWrap .progress-root{ ${str};height: auto !important;}`
          }
          preClass += `.${prefix}--progressWrap .ft--color-primary{ background-color: ${safeLighten(styleObject.color, 0.6)};${styleObject.lineWidth ? 'height: ' + styleObject.lineWidth + 'px;' : ''}}`
          preClass += `.${prefix}--progressWrap .ft--bar{ background-color: ${styleObject.color};${styleObject.lineWidth ? 'height: ' + styleObject.lineWidth + 'px;' : ''}}`
          styleObject.color = undefined
        }

        if (type === 'StarRating' && (k === 'iconSelected' || k === 'iconHover')) {
          return `span.${prefix}--${postFix}{${classBody}}${preClass}`
        } else if (
          (type === 'Stepper' || type === 'MultiStepper') &&
          ['iconActive', 'iconComplete', 'icon'].includes(k)
        ) {
          return `.${prefix}--${postFix}{${classBody}}${preClass} .${prefix}--iconRoot > .${prefix}--${postFix}{${classBody};color:${v.fill}}`
        } else if (type === 'Badge' && k === 'icon') {
          return `.${prefix}--${postFix}, .${prefix}--${postFix} > svg {${classBody}}${preClass}`
        } else if (type === 'eComCat' && k === 'searchInput') {
          return `.${prefix}--${postFix}, .${prefix}--${postFix}::placeholder{${classBody}}${preClass}`
        } else if (type === 'Link') {
          return `.${prefix}--${postFix}, .${prefix}--${postFix} > a {${classBody}}${preClass}`
        } else {
          return `.${prefix}--${postFix}{${classBody}}${preClass}`
        }
      } else {
        let classBody = ''
        let className = k
        if (isHidden) {
          classBody += ';display: none !important;'
        } else {
          classBody += ';'
        }
        classBody += '\n;box-sizing: border-box;'
        return `.${prefix}--${className}{
                ${classBody}
            }`
      }
    })
    return styleElem.join('\n')
  }
}
const objCompact = (obj) =>
  _.reduce(
    obj,
    (r, v, k) => ({
      ...r,
      ...(['top', 'left', 'right', 'bottom'].indexOf(k) !== -1 || typeof v !== 'undefined' ? { [k]: v } : {}),
    }),
    {}
  )
const isAllowState = (type, path) => {
  return (
    (String(path).indexOf('Item') !== -1 && type === 'ItemList') ||
    (String(path).indexOf('Item') !== -1 && type === 'Breadcrumb') ||
    (String(path).indexOf('root') !== -1 && type === 'PreviewAction') ||
    (String(path).indexOf('root') !== -1 && type === 'Button') ||
    (String(path).indexOf('root') !== -1 && type === 'Section') ||
    (String(path).indexOf('root') !== -1 && type === 'Stack') ||
    (String(path).indexOf('root') !== -1 && type === 'BarcodeReader') ||
    (String(path).indexOf('root') !== -1 && type === 'Capture') ||
    (String(path).indexOf('item') !== -1 && type === 'Rating') ||
    (String(path).indexOf('imageRoot') !== -1 && type === 'ImageChoice') ||
    (String(path).indexOf('root') === -1 && type === 'YesNo') ||
    (String(path).indexOf('item') === -1 && type === 'SurveyList') ||
    (String(path).indexOf('primary') !== -1 && type === 'Button') ||
    (String(path).indexOf('secondary') !== -1 && type === 'Button') ||
    (String(path).indexOf('apply') !== -1 && type === 'ConfirmationModal') ||
    (String(path).indexOf('cancel') !== -1 && type === 'ConfirmationModal') ||
    (String(path).indexOf('item') !== -1 && type === 'CES') ||
    (String(path).indexOf('root') !== -1 && type === 'Link') ||
    (String(path).indexOf('btn') !== -1 && ['Stepper', 'Stripe', 'MultiStepper'].includes(type)) ||
    // String(path).indexOf('ft__') === 0 ||
    String(path).indexOf('tab') !== -1 ||
    String(path).indexOf('backBtn') !== -1 ||
    String(path).indexOf('nextBtn') !== -1 ||
    String(path).indexOf('button') !== -1 ||
    String(path).indexOf('submitBtn') !== -1 ||
    String(path).indexOf('closeBtn') !== -1 ||
    String(path).indexOf('search') !== -1 ||
    String(path).indexOf('cancel') !== -1 ||
    String(path).indexOf('btn-prev') !== -1 ||
    String(path).indexOf('btn-next') !== -1 ||
    String(path).indexOf('btn-finish') !== -1 ||
    String(path).indexOf('apply') !== -1 ||
    String(path).indexOf('link') !== -1 ||
    String(path).indexOf('categoriesElement') !== -1 ||
    String(path).indexOf('subcategoriesElement') !== -1 ||
    String(path).indexOf('folderLink') !== -1 ||
    String(path).indexOf('folder') !== -1 ||
    String(path).indexOf('signInLink') !== -1 ||
    String(path).indexOf('profileLink') !== -1 ||
    iconClasses.includes(path) ||
    String(path).indexOf('overlay') !== -1 ||
    String(path).indexOf('categoriesItem') !== -1 ||
    String(path).indexOf('subCategoriesItem') !== -1 ||
    String(path).indexOf('prevBtn') !== -1 ||
    String(path).indexOf('icon') !== -1 ||
    String(path).indexOf('profileMenuLink') !== -1 ||
    String(path).indexOf('sForm-btn') !== -1 ||
    String(path).indexOf('tSlotBtn') !== -1 ||
    String(path).indexOf('tSlot') !== -1 ||
    String(path).indexOf('nextBtn') !== -1 ||
    String(path).indexOf('autoSuggest') !== -1 ||
    String(path).indexOf('actionBtn') !== -1 ||
    String(path).indexOf('trashBtn') !== -1 ||
    type === 'Svg' ||
    type === 'TextCounter' ||
    (type === 'ContinualForm' && (String(path).indexOf('backIco') !== -1 || String(path).indexOf('nextIco') !== -1))
  )
}
const IMPORTANT_CLASSES = [
  'SmartVVerificationPage',
  'SmartVLoginPage',
  'SmartVSignUpPage',
  'SmartVProfilePage',
  'form',
  'eComCat',
  'eComProd',
  'eComRelProd',
  'eProdDlg',
  'eComCheck',
  'eSumPanel',
  'eComCart',
  'eComCartPage',
  'ECommerceProduct',
  'ECommerceCatalogue',
  'SmartVLoginForm',
  'EComProdCtrl',
  'SmartVOtpForm',
  'ECommerceCategories',
  'EComCatCtrl',
  'EComCatCtrl-Default',
  'EComCatCtrl-Narrow',
  'eComSlide',
  'galleryCarousel',
  'ContinualForm',
  'ImageChoice',
  'CES',
  'eComPaymentDlg--Stripe',
  'eComPaymentDlg--Tranzila',
  'eComPaymentDlg--PayPal',
  'Rating',
  'Scheduler',
  'sProfile',
  'sMTypes',
  'sCalendar',
  'sTime',
  'sForm',
  'ConfirmationModal',
  'FlexStrip',
  'DrawerEl',
  'sfNotificationMsg',
  'Page',
  'CoBrowse',
  'chart',
  'Bot',
  'Matrix',
  'redirectModal',
  'IntDocGen',
  'Capture',
  'split-login',
  'fsplit-login',
  'csimple-login',
  'fcsimple-login',
  'split-reg',
  'fsplit-reg',
  'csimple-reg',
  'fcsimple-reg',
  'split-ver',
  'fsplit-ver',
  'csimple-ver',
  'fcsimple-ver',
  'Tracker',
]

/**
 *  Create an object where the key is a valid css var,
 *  options for target to concat a unit e.g or a default value
 * @param stylesArr
 * @param cssVars
 * @param path
 * @param target
 * @param concatDefaultValue
 */
const createCssVars = (stylesArr, cssVars, path, { target = '', concatDefaultValue = '' } = {}) => {
  const styles = _.keyBy(stylesArr, 'k')
  const value = _.get(styles, path)
  const shouldModifyValue = (target?.length || concatDefaultValue.length) && value !== 'auto'
  const modifyValue = (value, concatTarget, defaultValue, styles) =>
    `${value}${_.get(styles, concatTarget) || defaultValue}`
  const modValue = shouldModifyValue ? modifyValue(value, target, concatDefaultValue, styles) : value
  const createCssVarKey = (path) => `--${path?.split('.')?.join('-')?.replace('-v-', '-')}`
  if (value) {
    cssVars[createCssVarKey(path)] = modValue
  }
}

const addStyleClassesForMode = (
  prefix,
  styles,
  type,
  isHidden = false,
  stripRootOrigin,
  mode,
  isRTL = false,
  isPreview,
  isCutStrip = false,
  isTheme = false
) => {
  const PREVIEW_ENV = isPreview
  let important = IMPORTANT_CLASSES.indexOf(type) !== -1 ? '!important' : ''
  if (type == 'FormField') {
    let styleProps = _.first(styles)
    if (styleProps && styleProps.root) {
      styleProps.root = _.omit(styleProps.root, ['height'])
    }
  }
  if (['Strip', 'Header', 'Footer'].indexOf(type) !== -1) {
    let areas = _.filter(styles, (s) => Boolean(s.areas))
    const areaKeys = _.uniq(_.flatten(_.get(_.last(areas), 'areas', [])))
    let style = []

    const getStyles = (key) => {
      let s = {}
      _.each(styles, (v) => {
        s = _.assign(s, objCompact(_.get(v, key, {})))
      })
      if (isRTL) {
        return transformCSSStyle(s)
      }
      return s
    }
    let stripRoot = stripRootOrigin === '100%' && !isPreview ? `calc(${stripRootOrigin} - 30px)` : stripRootOrigin
    let maxStripRoot = 'var(--content-width)'
    if (!DRAWER_MOBILE.includes(mode) && isPreview && isCutStrip) {
      maxStripRoot = 'calc(var(--content-width) - var(--project-drawer-width))'
    }

    let rootStyle = getStyles('root')
    if (String(prefix).indexOf('Footer') !== -1) {
      if (rootStyle.height) {
        rootStyle.minHeight = rootStyle.height
        delete rootStyle.height
      }
    }
    let userStyle = rootStyle?.customCSS || ''
    let rootCompiledStyle = {
      ...getStyle(rootStyle),
      ...(isCutStrip
        ? {
            width: stripRoot,
            maxWidth: maxStripRoot,
          }
        : {
            maxWidth: stripRoot,
          }),
    }
    if (
      type === 'Strip' &&
      ~~styles[0].rows === 1 &&
      ~~styles[0].columns === 1 &&
      String(rootCompiledStyle.minHeight).indexOf('unset') !== -1
    ) {
      rootCompiledStyle.minHeight = transformCSSPositionValue(styles[0].row0, styles[0].row0Unit)
    }
    if (type === 'Strip') {
      if (mode !== 'lg' && isPreview) {
        rootCompiledStyle = _.omit(rootCompiledStyle, [...BACKGROUND_STYLES_KEYS, ...BORDER_KEYS])
      }
    }
    let rootClassBody = _.map(rootCompiledStyle, (val, key) => `${kebabCase(key)}:${val}`).join(';\n')
    rootClassBody += '\n;box-sizing: border-box;'
    if (type === 'Header' || type === 'Footer') {
      rootClassBody += '\n;z-index: 301;'
    }
    if (isHidden) {
      if (PREVIEW_ENV) {
        rootClassBody += '\n;display: none;'
      } else {
        rootClassBody += '\n;opacity: .8;'
      }
    }
    if (userStyle) {
      rootClassBody += `;${userStyle}`
    }
    if (PREVIEW_ENV && type === 'Header' && !_.get(styles, '0.isEnableSpread', true)) {
      rootClassBody += `min-width: unset;width: ${stripRoot};max-width: 100vw;margin: 0 auto;`
    }
    style.push(`.${prefix}--root{${rootClassBody}}`)
    if (!rootCompiledStyle.borderWidth && rootCompiledStyle.borderWidth != 0 && !rootStyle['--ft-border']) {
      rootClassBody += 'border-width: inherit;'
    }
    if (_.get(styles, '0.isEnableSpread', true)) {
      let additional = _.pick(getStyles(`ft__${_.first(areaKeys)}`), ['marginBottom', 'marginTop'])
      let additionalClassBody = _.map(getStyle(additional), (val, key) => `${kebabCase(key)}:${val}`).join(';\n')
      style.push(
        `.${prefix}--spreadPlaceholder{${rootClassBody};${additionalClassBody};width: 100%;max-width: 100%;position: relative;}`
      )
    } else {
      style.push(
        `.${prefix}--spreadPlaceholder{${rootClassBody};background: none;width: 100%;max-width: 100%;position: relative;border: none;border-radius: none;}`
      )
    }
    if (type === 'Header') {
      const heightRoot = _.get(styles, [0, 'root'], {})
      const height = getSizeProp(heightRoot, 'height')
      style.push(`.ft--theme--Header--first-strip-offset{
        margin-top: -${height} !important;
      }`)
    }

    let spreadStyle =
      ['Header', 'Footer'].indexOf(type) === -1
        ? {
            ...getStyle({
              ..._.omit(rootStyle, [...BACKGROUND_STYLES_KEYS, ...BORDER_KEYS]),
              background: 'none',
              backgroundColor: 'transparent',
            }),
            ...(['Header', 'Footer'].indexOf(type) !== -1
              ? PREVIEW_ENV
                ? {
                    width: '100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                    ..._.pick(rootStyle, [...BACKGROUND_STYLES_KEYS, ...BORDER_KEYS]),
                  }
                : {
                    width: '100%',
                    minWidth: 'auto',
                    maxWidth: '100%',
                    ..._.pick(rootStyle, [...BACKGROUND_STYLES_KEYS, ...BORDER_KEYS]),
                  }
              : {}),
            ...(mode !== 'lg' && _.get(styles, '0.isEnableSpread', true)
              ? {
                  marginLeft: 'var(--strip-content-margin)',
                  marginRight: 'var(--strip-content-margin)',
                  maxWidth: stripRoot ? stripRoot : '100%',
                }
              : {}),
          }
        : {
            ...getStyle(_.omit(rootStyle, [...BACKGROUND_STYLES_KEYS, ...BORDER_KEYS])),
            ...(['Header', 'Footer'].indexOf(type) !== -1
              ? PREVIEW_ENV
                ? {
                    width: '100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                  }
                : {
                    width: '100%',
                    minWidth: 'auto',
                    maxWidth: '100%',
                  }
              : {}),
          }
    const stripSpreadRootClassBody = _.map(spreadStyle, (val, key) => `${kebabCase(key)}:${val}`).join(';\n')
    style.push(`.${prefix}--stripSpreadRoot{${stripSpreadRootClassBody}}`)

    let stripSpreadRoot = {
      ...getStyle(
        _.pick(rootStyle, [
          ...(_.get(styles, '0.isEnableSpread', true) ? [...BACKGROUND_STYLES_KEYS, ...BORDER_KEYS] : []),
          'minHeight',
          'minHeightUnit',
        ])
      ),
      ...(PREVIEW_ENV
        ? {
            width: '100%',
            minWidth: '100%',
            maxWidth: '100%',
            position: 'relative',
            // marginLeft: `calc(${stripRoot} / 2 - 50%)`,
            // marginRight: `calc(${stripRoot} / 2 - 50%)`
          }
        : {}),
    }
    // if(prefix ==='ft--Footer'){
    //     stripSpreadRoot.marginLeft = 'auto'
    //     stripSpreadRoot.marginRight = 'auto'
    // }

    if (type === 'Strip') {
      if (mode !== 'lg' && isPreview) {
        stripSpreadRoot = _.omit(stripSpreadRoot, [...BACKGROUND_STYLES_KEYS, ...BORDER_KEYS])
        if (_.endsWith(stripSpreadRoot.minHeight, '%') && !stripSpreadRoot.height) {
          stripSpreadRoot.height = stripSpreadRoot.minHeight
        }
      }
    }
    let spreadRootClassBody = _.map(stripSpreadRoot, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`).join(';\n')
    style.push(`.${prefix}--spreadRoot{${spreadRootClassBody}}`)

    let omitKeys = SPREAD_CONTENT_OMIT
    if (type === 'Strip') {
      omitKeys = mode === 'lg' ? SPREAD_CONTENT_OMIT : SPREAD_CONTENT_PREVIEW_OMIT
    }
    if (['ss', 'xs', 'sm'].includes(mode) && rootStyle.minHeightUnit === '%') {
      rootStyle.height = '100'
      rootStyle.heightUnit = '%'
    }
    if (rootStyle.overflowY === 'auto' && ['lg', 'md', 'sml'].includes(mode) && rootStyle.minHeight) {
      rootStyle.height = rootStyle.minHeight
      rootStyle.heightUnit = rootStyle.minHeightUnit
    }
    const spreadContentClassBody = _.map(
      {
        ...getStyle(_.omit(rootStyle, omitKeys)),
        marginLeft: 'var(--strip-content-margin)',
        marginRight: 'var(--strip-content-margin)',
        ...(['Header', 'Footer'].indexOf(type) !== -1
          ? {
              width: '100%',
              minWidth: '100%',
              maxWidth: '100%',
            }
          : {
              // width: '100%',
              // minWidth: '100%',
              // maxWidth: stripRoot ? stripRoot : '100%',
              ...(isCutStrip
                ? {
                    width: stripRoot,
                    maxWidth: maxStripRoot,
                    overflow: 'hidden',
                  }
                : {
                    width: '100%',
                    maxWidth: stripRoot ? stripRoot : '100%',
                    ...(mode !== 'lg' && _.get(styles, '0.isEnableSpread', true)
                      ? {
                          maxWidth: '100%',
                          margin: 0,
                        }
                      : {}),
                  }),
              // maxWidth: maxStripRoot
            }),
      },
      (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`
    ).join(';\n')
    style.push(`.${prefix}--spreadContent{${spreadContentClassBody}}`)
    if (!isPreview) {
      if (['ss', 'xs', 'sm'].includes(mode) && rootStyle.minHeightUnit === '%') {
        rootStyle.height = 'auto'
        rootStyle.heightUnit = '%'
      }
      const builderSpreadContentClassBody = _.map(
        {
          ...getStyle(_.omit(rootStyle, omitKeys)),
          marginLeft: 'var(--strip-content-margin)',
          marginRight: 'var(--strip-content-margin)',
          ...(['Header', 'Footer'].indexOf(type) !== -1
            ? {
                width: '100%',
                minWidth: '100%',
                maxWidth: '100%',
              }
            : {
                // width: '100%',
                // minWidth: '100%',
                // maxWidth: stripRoot ? stripRoot : '100%',
                ...(isCutStrip
                  ? {
                      width: stripRoot,
                      maxWidth: maxStripRoot,
                      overflow: 'hidden',
                    }
                  : {
                      width: '100%',
                      maxWidth: stripRoot ? stripRoot : '100%',
                      ...(mode !== 'lg' && _.get(styles, '0.isEnableSpread', true)
                        ? {
                            maxWidth: '100%',
                            margin: 0,
                          }
                        : {}),
                    }),
                // maxWidth: maxStripRoot
              }),
        },
        (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`
      ).join(';\n')
      style.push(
        `#ft__builder__root__el .ft--layout--xs .${prefix}--root > .ft--control--root, #ft__builder__root__el .ft--layout--ss .${prefix}--root > .ft--control--root, #ft__builder__root__el .ft--layout--sm .${prefix}--root > .ft--control--root {height: 100%;}`
      )
      style.push(`#ft__builder__root__el .${prefix}--spreadContent{${builderSpreadContentClassBody}}`)
    }
    if (['Header', 'Footer'].indexOf(type) !== -1) {
      let styleProps = {}
      _.each(styles, (v) => {
        _.each(v, (vv, k) => {
          if (isObject(vv)) {
            styleProps[k] = styleProps[k] || {}
            styleProps[k] = _.assign(styleProps[k], objCompact(vv))
          }
        })
      })
      _.map(styleProps, (v, k) => {
        let localImportant = important
        if (type === 'Header' && ['icon', 'badge', 'linkSelected'].includes(k)) {
          localImportant = '!important'
        }
        if (_.isObject(v) && k != 'root') {
          let styleObject = {
            ...v,
          }
          if (styleObject.fill && _.startsWith(k, 'iconLink')) {
            styleObject['--ft-icon-fill'] = styleObject.fill
          } else if (styleObject.color && _.startsWith(k, 'iconLink')) {
            styleObject['--ft-icon-fill'] = styleObject.color
          }
          let userStyle = styleObject.customCSS || ''
          delete styleObject.customCSS
          if (isRTL) {
            styleObject = transformCSSStyle(v)
          }
          styleObject = getStyle({ ...styleObject })
          let classBody = _.map(styleObject, (val, key) => {
            return `${kebabCase(key)}:${_.trim(val, ';')}${localImportant}`
          })

          classBody = classBody.join(';\n')
          if (userStyle) {
            classBody += `;${userStyle}`
          }
          let className = k
          if (String(k).indexOf('underline') !== -1) {
            className += ':after'
            if (v.visibility === 'hidden') {
              preClass = `.${prefix}--${k}:before{
                visibility: hidden;
              }
              .${prefix}--${k}{
                border-bottom: none;
              }
              .${prefix}--${k}:hover{
                border-bottom: none;
              }`
            } else {
              let styleObject = _.omit(getStyle(v), ['activeBorderColor', 'activeBorderWidth'])
              let defaultClassBody = _.map(styleObject, (val, key) => {
                return `${kebabCase(key)}:${_.trim(val, ';')}`
              })
              preClass = `.${prefix}--${k}:before{
                ${defaultClassBody}
              }
              .${prefix}--${k}{
                ${defaultClassBody}
              }
              .${prefix}--${k}:hover:before{
                visibility: hidden;
                ${defaultClassBody}
              }`
            }
            styleObject = _.pick(getStyle(v), ['activeBorderColor', 'activeBorderWidth'])
            styleObject.borderColor = styleObject.activeBorderColor
            styleObject.borderWidth = styleObject.activeBorderWidth || '1px'
            styleObject.borderStyle = 'solid'
            _.unset(styleObject, 'activeBorderColor')
            _.unset(styleObject, 'activeBorderWidth')
            classBody = _.map(styleObject, (val, key) => {
              return `${kebabCase(key)}:${_.trim(val, ';')}!important`
            })
            classBody = classBody.join(';\n')
            preClass += `.${prefix}--${k}:hover:after{
              transform: none;
              ${classBody}
            }`
          }
          if (_.endsWith(className, 'Active')) {
            className = String(className).replace('Active', ':active')
          } else if (_.endsWith(className, 'Hover')) {
            className = String(className).replace('Hover', ':hover')
          }
          if (isHidden) {
            classBody += '\n;display: none !important;'
          } else {
            classBody += '\n;'
          }
          classBody += '\n;box-sizing: border-box;'
          style.push(`.${prefix}--${className}{
                  ${classBody}
              }`)
        }
      })
    }

    let overlayStyle = getStyles('overlay')
    if (!_.isEmpty(overlayStyle)) {
      const compiledStyle = getStyle(overlayStyle)
      let classBody = _.map(compiledStyle, (val, key) => {
        return `${kebabCase(key)}:${_.trim(val, ';')}`
      }).join(';')
      let overlayClass = `position: absolute;
left: 0;
right: 0;
top: 0;
bottom: 0;
content: "";
${classBody};
width: auto;
height: auto;`
      if (_.get(styles, '0.isEnableSpread', true) && mode === 'lg') {
        if (isPreview) {
          style.push(`.${prefix}--spreadRoot:after{${overlayClass}}`)
        } else {
          style.push(`.${prefix}--spreadPlaceholder:after{${overlayClass}}`)
        }
      } else {
        if (isPreview) {
          style.push(`.${prefix}--spreadRoot:after{${overlayClass}}`)
        } else {
          style.push(`.${prefix}--root:after{pointer-events: none;${overlayClass}}`)
        }
      }
    }

    let overlayStyleActive = getStyles('overlayActive')
    if (!_.isEmpty(overlayStyleActive)) {
      const compiledStyle = getStyle(overlayStyleActive)
      let key = ':active'
      let classBody = _.map(compiledStyle, (val, key) => {
        return `${kebabCase(key)}:${_.trim(val, ';')}`
      }).join(';')
      let overlayClass = `position: absolute;
left: 0;
right: 0;
top: 0;
bottom: 0;
content: "";
${classBody};
width: auto;
height: auto;`
      if (_.get(styles, '0.isEnableSpread', true)) {
        style.push(`.${prefix}--spreadPlaceholder${key}:after{${overlayClass}}`)
      } else {
        style.push(`.${prefix}--root${key}:after{z-index: -1;${overlayClass}}`)
      }
    }
    let overlayStyleHover = getStyles('overlayHover')
    if (!_.isEmpty(overlayStyleHover)) {
      const compiledStyle = getStyle(overlayStyleHover)
      let key = ':hover'
      let classBody = _.map(compiledStyle, (val, key) => {
        return `${kebabCase(key)}:${_.trim(val, ';')}`
      }).join(';')
      let overlayClass = `position: absolute;
left: 0;
right: 0;
top: 0;
bottom: 0;
content: "";
${classBody};
width: auto;
height: auto;`
      if (_.get(styles, '0.isEnableSpread', true)) {
        style.push(`.${prefix}--spreadPlaceholder${key}:after{${overlayClass}}`)
      } else {
        style.push(`.${prefix}--root${key}:after{z-index: -1;${overlayClass}}`)
      }
    }

    if (String(prefix).indexOf('Footer') !== -1) {
      let minH = transformCSSPositionValue(_.get(styles, '[0].root.height'), _.get(styles, '[0].root.heightUnit'))
      if (String(prefix).indexOf('theme') === -1) {
        minH &&
          minH !== 'unset' &&
          style.push(
            `.${prefix}--root > .ft--control--root > .ft--Footer--stripSpreadRoot{min-height: ${minH} !important;}`
          )
        minH &&
          style.push(
            `.${prefix}--root > .ft--control--root > .ft--Footer--spreadContent{min-height: ${minH} !important;}`
          )
        minH && style.push(`.${prefix}--root.ft--Footer--spreadPlaceholder{min-height: ${minH} !important;}`)
      }
      if (!_.get(styles, '0.isEnableSpread', true)) {
        if (mode === 'lg') {
          style.push(`.${prefix}--stripSpreadRoot {${rootClassBody}}`)
        } else {
          style.push(`.${prefix}--stripSpreadRoot {${rootClassBody}}`)
        }
      }
    }

    _.each(areaKeys, (area) => {
      const styleObj = getStyles(`ft__${area}`)
      if (['ss', 'xs', 'sm'].includes(mode) && rootStyle.minHeightUnit === '%' && !styleObj.maxHeight) {
        styleObj.maxHeight = '100%'
      }
      const classBody = _.map(getStyle(styleObj), (val, key) => `${kebabCase(key)}:${val}`).join(';\n')
      style.push(`.${prefix}--${area}{${classBody}}`)

      const styleObjHover = getStyles(`ft__${area}Hover`)
      const classBodyHover = _.map(getStyle(styleObjHover), (val, key) => `${kebabCase(key)}:${val}`).join(';\n')
      style.push(`.${prefix}--${area}:hover{${classBodyHover}}`)

      const styleObjActive = getStyles(`ft__${area}Active`)
      const classBodyActive = _.map(getStyle(styleObjActive), (val, key) => `${kebabCase(key)}:${val}`).join(';\n')
      style.push(`.${prefix}--${area}:active{${classBodyActive}}`)
    })
    return style.join('\n')
  } else {
    let styleProps = _.first(styles)
    // _.each(styles, v => {
    //   _.each(v, (vv, k) => {
    //     if (isObject(v)) {
    //       styleProps[k] = styleProps[k] || {}
    //       styleProps[k] = _.assign(styleProps[k], objCompact(vv))
    //     }
    //   })
    // })
    let stylesArr = []
    _.each(styleProps, (v, k) => {
      if (v && _.isObject(v)) {
        v = _.omit(v, ['position'])
        if (k === 'rootHover' || k === 'rootActive') {
          v = _.omit(v, ['justifySelf'])
          v = _.omit(v, ['alignSelf'])
        }
      }
      stylesArr.push({
        k: k,
        v: v,
      })
    })

    stylesArr = _.sortBy(stylesArr, (style) =>
      toLS(style.k).indexOf('selected') !== -1 ||
      toLS(style.k).indexOf('active') !== -1 ||
      toLS(style.k).indexOf('complete') !== -1 ||
      toLS(style.k).indexOf('hover') !== -1 ||
      toLS(style.k).indexOf('disabled') !== -1 ||
      toLS(style.k).indexOf('arow') !== -1
        ? 1
        : -1
    )
    stylesArr = _.sortBy(stylesArr, (style) => {
      if (type == 'PowerTable') {
        if (style.k === 'thr') {
          return 1000
        } else if (String(style.k).includes('labelComp')) {
          return 1000
        } else if (String(style.k).includes('hcol')) {
          return 1200
        } else if (String(style.k).includes('filtercol')) {
          return 1600
        } else if (String(style.k).includes('filterthr') || String(style.k).includes('globthr')) {
          return 1400
        } else if (String(style.k).includes('tfc-')) {
          return 1500
        } else if (String(style.k).includes('tfc')) {
          return 1400
        } else if (String(style.k).includes('tf')) {
          return 1600
        } else if (String(style.k).includes('thr')) {
          return 1200
        }
        return -1
      } else if (type == 'Tab') {
        if (style.k == 'root') {
          return -2
        } else {
          return -1
        }
      } else if (CHART_TOP_CLASS.includes(type)) {
        return 1200
      } else if (style.k === 'iconHover') {
        return 1200
      } else if (style.k === 'iconSelected') {
        return 1000
      } else if (toLS(style.k).indexOf('icon') !== -1) {
        return 1
      } else if (toLS(style.k).indexOf('disabled') !== -1) {
        return 2
      } else if (
        toLS(style.k).indexOf('selected') !== -1 ||
        toLS(style.k).indexOf('active') !== -1 ||
        toLS(style.k).indexOf('complete') !== -1 ||
        toLS(style.k).indexOf('disabled') !== -1
      ) {
        return 1
      } else {
        return -1
      }
    })
    const cssVars = {}
    if (type === 'Tracker') {
      createCssVars(stylesArr, cssVars, 'root.v.height', { concatDefaultValue: 'px' })
      createCssVars(stylesArr, cssVars, 'root.v.width', { target: 'root.v.widthUnit' })
      createCssVars(stylesArr, cssVars, 'root.v.borderRadius', { concatDefaultValue: 'px' })
      createCssVars(stylesArr, cssVars, 'root.v.marginBottom', { concatDefaultValue: 'px' })
      createCssVars(stylesArr, cssVars, 'root.v.paddingBottom', { concatDefaultValue: 'px' })
      createCssVars(stylesArr, cssVars, 'root.v.expandedMinHeight', { concatDefaultValue: 'px' })
      createCssVars(stylesArr, cssVars, 'root.v.expandedMinWidth', { concatDefaultValue: 'px' })
      createCssVars(stylesArr, cssVars, 'scrollbar.v.scrollAreaSize', {
        target: 'scrollbar.v.scrollAreaSizeUnit',
        concatDefaultValue: 'px',
      })
      createCssVars(stylesArr, cssVars, 'scrollbar.v.width', { concatDefaultValue: 'px' })
      createCssVars(stylesArr, cssVars, 'scrollGradient.v.height', {
        target: 'scrollGradient.v.heightUnit',
        concatDefaultValue: 'px',
      })
      createCssVars(stylesArr, cssVars, 'scrollGradient.v.background')
      createCssVars(stylesArr, cssVars, 'groupBullet.v.backgroundColor')
      createCssVars(stylesArr, cssVars, 'iconComplete.v.fill')
      createCssVars(stylesArr, cssVars, 'itemCompleteIcon.v.fill')
      createCssVars(stylesArr, cssVars, 'itemBullet.v.backgroundColor')
      createCssVars(stylesArr, cssVars, 'trackerProgressBar.v.backgroundColor')
      createCssVars(stylesArr, cssVars, 'trackerProgressBar.v.color')
      createCssVars(stylesArr, cssVars, 'trackerProgressBar.v.lineWidth', {
        target: 'trackerProgressBar.v.lineWidthUnit',
        concatDefaultValue: 'px',
      })
      createCssVars(stylesArr, cssVars, 'trackerProgressBar.v.backgroundColor')
      createCssVars(stylesArr, cssVars, 'expandIcon.v.fill')
      createCssVars(stylesArr, cssVars, 'collapseIcon.v.fill')
      createCssVars(stylesArr, cssVars, 'groupBullet.v.height', { concatDefaultValue: 'px' })
      createCssVars(stylesArr, cssVars, 'groupBullet.v.width', { concatDefaultValue: 'px' })
      createCssVars(stylesArr, cssVars, 'itemBullet.v.height', { concatDefaultValue: 'px' })
      createCssVars(stylesArr, cssVars, 'itemBullet.v.width', { concatDefaultValue: 'px' })
      createCssVars(stylesArr, cssVars, 'item.v.paddingLeft', { concatDefaultValue: 'px' })
      createCssVars(stylesArr, cssVars, 'item.v.paddingRight', { concatDefaultValue: 'px' })
      createCssVars(stylesArr, cssVars, 'item.v.expandIcon.fill')
      createCssVars(stylesArr, cssVars, 'item.v.collapseIcon.fill')
      createCssVars(stylesArr, cssVars, 'item.v.backgroundColor')
      createCssVars(stylesArr, cssVars, 'line.v.borderColor')
    }
    return _.map(stylesArr, ({ v, k }) => {
      if (isTheme) {
        if (toLS(k).indexOf('active') !== -1 || toLS(k).indexOf('hover') !== -1 || toLS(k).indexOf('disabled') !== -1) {
          _.each(ALL_SIZE_KEYS, (key) => {
            _.unset(v, key)
          })
        }
      }
      if (type === 'Typography' && (_.endsWith(k, 'Active') || _.endsWith(k, 'Hover'))) {
        return ''
      }
      if (type === 'FormField' && ['endIcon', 'startIcon'].includes(k) && v.fontSize) {
        v.width = v.fontSize
        v.height = v.fontSize
      }
      if (type === 'CES' && String(k).includes('item')) {
        v.justifyContent = textAlignToFlex(v.textAlign)
      }
      if (type === 'Stack' && _.startsWith(k, 'root') && k !== 'root') {
        v.maxWidth = undefined
      }
      if (type !== 'PowerTable') {
        if (k === 'checkbox') {
          v.color = v.fill + '!important'
        }
        if (k === 'activeTab' || String(k).indexOf('tra') !== -1) {
          important = '!important'
        } else {
          important = IMPORTANT_CLASSES.indexOf(type) !== -1 ? '!important' : ''
        }
        if (type === 'Accordion' && ['acrdWrap', 'expanded', 'itemSelected'].includes(k)) {
          important = '!important'
        }
        if (type === 'CarouselContainer' && k !== 'content') {
          important = '!important'
        }
        if (type === 'Form' && _.startsWith(k, 'col-')) {
          important = '!important'
        }
        if (type === 'Stepper' && k === 'stepContent') {
          _.unset(v, 'height')
        }
      } else if (
        type === 'PowerTable' &&
        (String(k).indexOf('thr') !== -1 ||
          String(k).indexOf('col') !== -1 ||
          String(k).indexOf('tfc') !== -1 ||
          String(k).indexOf('tf') !== -1 ||
          String(k).indexOf('thcss') !== -1 ||
          String(k).indexOf('icon') !== -1 ||
          k === 'paginationBtn' ||
          k === 'checkbox')
      ) {
        important = '!important'
      } else if (type === 'PowerTable' && k === 'td') {
        important = ''
      } else if (type === 'PowerTable' && k === 'resizer') {
        important = '!important'
      }
      if (type === 'Stepper' && k === 'label') {
        important = '!important'
      }
      if (type === 'FormField' && k == 'item') {
        important = '!important'
      }
      if (_.endsWith(k, 'Active')) {
        important = '!important'
      }
      if (type === 'StarRating' && String(k).includes('icon')) {
        important = '!important'
        if (['xs', 'ss'].includes(mode)) {
          v.maxHeight = v.height
          v.maxWidth = v.width
        }
      }
      if (type === 'Accordion' && k === 'root') {
        important = '!important'
      }
      if (_.endsWith(k, 'autoSuggestSelected')) {
        important = '!important'
      }
      if (_.endsWith(k, 'listAutoComplete')) {
        important = '!important'
      }
      if (type === 'StarRating' && (k === 'iconSelected' || k === 'iconHover')) {
        important = '!important'
      }
      if (type === 'Stepper' && k === 'labelComp') {
        important = '!important'
      }
      if (CHART_TOP_CLASS.includes(type)) {
        important = '!important'
      }
      if (type === 'chart' && !String(prefix).includes('chart')) {
        important = '!important'
      } else if (type === 'chart' && String(prefix).includes('chart')) {
        important = ''
      }
      if (k === 'legendCol') {
        important = ''
      }
      if (type === 'PowerTable' && String(k).indexOf('icon') !== -1) {
        important = '!important'
      }
      if (_.isObject(v)) {
        // if(type === 'Form' && k === 'root' && !v.minWidth){
        //     v.minWidth = 'min-content'
        // }
        if (['incIcon', 'decIcon', 'trashIcon', 'trashIconHover', 'trashIconActive'].includes(k)) {
          if (v.fill) {
            v.color = v.fill
          }
        }
        if (type === 'Scroll' && k === 'root' && v.gridGap) {
          v['--grid-gap'] = v.gridGap
        }
        if (String(k).includes('root') && v.opacity && important !== '!important') {
          v.opacity = v.opacity + '!important'
        }
        if (
          ((String(k).includes('sortIcon') || k === 'checkbox' || k === 'groupIcon') && type === 'PowerTable') ||
          k === 'addonCheckbox' ||
          k === 'customIcon'
        ) {
          v.color = v.fill
        }
        if ((k === 'icon' && v && typeof v == 'object') || type === 'Svg') {
          v['--ft-icon-fill'] = v.fill
          v['--ft-icon-stroke'] = v.stroke
        }
        if (mode !== 'lg' && type === 'FlexStrip') {
          delete v.paddingLeft
          delete v.paddingRight
        }
        let userStyle = v.customCSS || ''
        delete v.customCSS
        if (type === 'Modal' && k === 'root') {
          if (v.width === 'auto') {
            v.gridTemplateColumns = 'min-content'
            v.maxHeight = 'none'
            if (v.maxWidth) {
              v.overflowX = 'auto'
            }
          }
        }
        if (['ExternalLookup', 'FormField', 'Search', 'AddressFinder'].indexOf(type) !== -1 && k === 'root') {
          if (!v.height) {
            v.height = 'min-content'
          }
        }
        if (['Scheduler', 'Tracker'].includes(type) && _.endsWith(k, '-line')) {
          delete v.width
        }
        if ((type === 'AddressFinder' || type === 'ExternalLookup') && k === 'root') {
          if (v.height !== 'auto' && v.height !== 'min-content') {
            v.height = 'min-content'
          }
        }
        if (type === 'Search' && k === 'root') {
          if (v.height !== 'auto' && v.height !== 'min-content') {
            v.height = 'min-content'
          }
        }
        if (type === 'Range' && k === 'thumb') {
          if (v.backgroundColor) {
            v.color = v.backgroundColor
          }
        }
        if (type === 'Tracker' && k === 'track') {
          if (v.backgroundColor) {
            v['--progress-fill'] = v.backgroundColor
          }
          if (v.height) {
            v['--progress-track-size'] = v.height
          }
        }
        if (type === 'Tracker' && k === 'root') {
          v = {
            ...v,
            ...cssVars,
          }
        }
        if (type === 'Tracker' && k === 'groupItem') {
          const { paddingLeft, paddingRight } = _.pick(v, ['paddingLeft', 'paddingRight'])
          v['--item--padding'] = (isRTL ? paddingLeft : paddingRight) - 2 + 'px'
        }

        let rootClassBody = ''
        if (isRTL) {
          v = transformCSSStyle(v, type === 'table')
        }
        if (type === 'Ticker' && k === 'icon') {
          v.color = v.fill
        }
        let preClass = ''
        if (type === 'chart' && k === 'chartTooltip') {
          preClass += `.${prefix}--chartTooltip{--bg-color: ${v.backgroundColor};}`
          const boxShadow = getStyle(v).boxShadow
          if (boxShadow) {
            preClass += `.${prefix}--chartTooltipArrow::after{box-shadow: ${boxShadow};}`
          }
        }
        if (type === 'MultiStepper' && k === 'root') {
          let paddingStyles = _.pick(getStyle(v), ['paddingTop', 'paddingBottom'])
          const valList = _.values(paddingStyles)
          let str = ''
          if (_.compact(valList).length === valList.length && valList.length > 0) {
            preClass += `.${prefix}--root.ftb-resizers.MultiStepper{${_.map(paddingStyles, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')} !important`).join(';\n')}}`
          }
          if (paddingStyles.paddingTop) {
            str += `--ctrl-padding-top:${paddingStyles.paddingTop};`
          }
          if (paddingStyles.paddingBottom) {
            str += `--ctrl-padding-bottom:${paddingStyles.paddingBottom};`
          }
          if (str) {
            preClass += `.${prefix}--root.ftb-resizers.MultiStepper{${str}}`
          }
        }
        if (type === 'Tracker' && k === 'track') {
          let size = _.pick(v, ['width', 'height'])
          v = _.omit(v, ['width', 'height'])

          if (size.width || size.height) {
            let itemStyleV = ''
            let itemStyleH = ''
            if (size.height) {
              itemStyleH = `width: ${size.height}  !important;height: ${size.height}  !important;`
            }
            if (size.width) {
              itemStyleV = `width: ${size.width}  !important;height: ${size.width}  !important;`
            }
            preClass += `.ft--theme--Tracker--item.active .${prefix}--track { width: ${size.width || '100%'} !important; height: ${size.height} !important; }`
            if (itemStyleV) {
              preClass += `.ft--theme--Tracker--column .${prefix}--track {${itemStyleV}}`
            }
            if (itemStyleH) {
              preClass += `.ft--theme--Tracker--row .${prefix}--track {${itemStyleH}}`
            }
          }
        }

        if (type === 'VerticalCard' && k === 'icon') {
          if (v && v.flexDirection) {
            preClass += `.${prefix}--header {flex-direction: ${v.flexDirection}; justify-content: flex-end !important;}`
            delete v.flexDirection
          }
        }

        let styleObject = getStyle(v)

        if (type === 'Accordion' && k === 'icon') {
          if (v.alignItems === 'flex-start') {
            preClass += `.${prefix}--acrdPanel { flex-direction: ${'row-reverse'} !important;  padding: 0 16px 0 0;}`
          }
          if (v.alignItems === 'flex-end') {
            preClass += `.${prefix}--acrdPanel { flex-direction: ${'row'} !important;}`
          }
        }
        if (type === 'Tab' && k === 'scrollButtonsSvg') {
          preClass += `.${prefix}--scrollButtons svg {${_.map(
            styleObject,
            (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`
          ).join(';\n')}}`
        }
        if (type === 'Button' && (_.includes(k, 'root') || _.includes(k, 'primary') || _.includes(k, 'secondary'))) {
          if (v.textAlign) {
            preClass += `.${prefix}--${k} .button-content { justify-content: ${v.textAlign} !important;}`
          }
        }
        if (k === 'checkbox' && type === 'PowerTable') {
          preClass += `.${prefix}--${k} svg{${_.map(
            styleObject,
            (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}${important}`
          ).join(';\n')}}`
        }
        if (type === 'Stepper' && k === 'line') {
          let vLine = `.${prefix}--vLine{`
          if (v.borderColor) {
            vLine += `border-left-color: ${v.borderColor};`
          }
          if (v.borderStyle) {
            vLine += `border-left-style: ${v.borderStyle};`
          }
          if (v.borderWidth) {
            vLine += `border-left-width: ${v.borderWidth}px;`
          }
          vLine += '}'
          let prefBody = getStyle(_.omit(v, ['borderColor', 'borderStyle', 'borderWidth']))
          const prefStyle = _.map(prefBody, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`).join(';\n')
          vLine += `.${prefix}--vLine::before{
                        content:'';
                        position: absolute;
                        display: block;
                        left: 0px;
                        top: 0px;
                        ${prefStyle}
                    }`
          preClass += vLine
        }
        if (k === 'listAutoComplete') {
          let scrollStyles = getScrollBarStyles(v)
          let str = _.map(scrollStyles, (val, key) => `${key}:${_.trim(val, ';')}`).join(';')
          preClass += `.${prefix}--${k}{${str}}`
        }
        if (type === 'PowerTable' && (_.startsWith(k, 'hcol') || _.startsWith(k, 'col')) && v.textAlign) {
          preClass += `.${prefix}--${k} > div {justify-content: ${textAlignToFlex(v.textAlign)}}`
        }
        if (type == 'Page') {
          if (!styleObject.width) {
            styleObject.width = stripRootOrigin
          }
          if (type === 'Page' && mode !== 'lg') {
            styleObject.width = stripRootOrigin
          }
          let scrollStyles = getScrollBarStyles(v)
          v = _.assign({}, v, scrollStyles)
          let pageID = k === 'root' ? '' : `[data-page="${k}"]`
          preClass += `#ft__builder__root__el > .ft--scroll--content${pageID}, .ft--theme--Window--root.ft--scroll--content{${_.map(
            scrollStyles,
            (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`
          ).join(';\n')}}`
        } else if (prefix == 'ft--theme--Section' || prefix == 'ft--theme--Stack') {
          if (!_.endsWith(k, 'Active') && !_.endsWith(k, 'Hover')) {
            if (!styleObject.width) {
              styleObject.width = '300px'
            }
            if (type == 'Stack') {
              if (!styleObject['height']) {
                styleObject['min-height'] = '300px'
              } else {
                styleObject['min-height'] = styleObject['height']
                delete styleObject['height']
              }
            } else if (!styleObject.height) {
              styleObject.height = '300px'
            }
          }
        }
        if (type === 'Stack') {
          if (styleObject.height) {
            styleObject['min-height'] = styleObject.height
            delete styleObject.height
          }
        }
        if (type == 'Typography' && k == 'root') {
          if (styleObject.height !== '' && styleObject.height !== 'auto') {
            styleObject.overflow = 'auto'
          }
        }
        if ((type === 'PowerTable' && k === 'arow') || (type == 'table' && k == 'tra')) {
          if (styleObject.backgroundColor) {
            styleObject['--cond-bg'] = styleObject.backgroundColor
            styleObject.backgroundColor = 'var(--cond-bg)'
          }
          if (styleObject.color && !(type == 'table' && k == 'tra')) {
            styleObject['--cond-c'] = styleObject.color
            styleObject.color = 'var(--cond-c)'
          }
        }
        if (type === 'Modal' && (_.endsWith(k, 'Hover') || _.endsWith(k, 'Active'))) {
          _.unset(styleObject, ['width'])
          _.unset(styleObject, ['minWidth'])
          _.unset(styleObject, ['maxWidth'])
          _.unset(styleObject, ['height'])
          _.unset(styleObject, ['minHeight'])
          _.unset(styleObject, ['maxHeight'])
        }
        rootClassBody = _.map(
          styleObject,
          (val, key) =>
            `${kebabCase(key)}:${_.trim(val, ';')}${(!['galleryCarousel', 'Rating'].includes(type) && k === 'root' && key === 'width') || _.startsWith(key, '--') || (type === 'PowerTable' && ['width', 'height'].includes(key) && k !== 'icon') ? '' : important}`
        ).join(';\n')
        let className = k
        if (String(k).indexOf('underline') !== -1) {
          const elStyle = `position: relative;`
          rootClassBody = elStyle
          const elAfterStyle = `
          left: 0;
          right: 0;
          bottom: 0;
          content: "\\00a0";
          position: absolute;
          border-bottom: ${typeof v.borderWidth === 'undefined' ? '1px' : v.borderWidth}px solid ${typeof v.borderColor === 'undefined' ? 'rgba(0, 0, 0, 0.42)' : v.borderColor};
          pointer-events: none;`
          preClass += `.${prefix}--${k}:before {${elAfterStyle}}
          .${prefix}--${k}:after {display: none;}
          .${prefix}--${k}:hover:before{
            border-bottom-color: ${v.activeBorderColor} !important;
            border-bottom-width: ${v.activeBorderWidth}px !important;
          }`
          if (v.visibility === 'hidden') {
            preClass += `.${prefix}--${k}:before, .${prefix}--${k}:after {display: none;} `
          }
        }
        if (_.endsWith(className, 'Hover') && type == 'TextCounter' && String(k).includes('trashIconHover')) {
          className =
            String(className).replace('Hover', ':hover') +
            `,.${prefix}--trashBtn:hover .${prefix}--${String(className).replace('Hover', '')}`
        } else if (_.endsWith(className, 'Active') && type == 'TextCounter' && String(k).includes('trashIconActive')) {
          className =
            String(className).replace('Active', ':active') +
            `,.${prefix}--trashBtn:active .${prefix}--${String(className).replace('Active', '')}`
        } else if (_.endsWith(className, 'Hover') && type == 'Range') {
          className = String(className).replace('Hover', ':hover')
        } else if (
          _.endsWith(className, 'Active') &&
          ['YesNo', 'ImageChoice', 'CES', 'SurveyList', 'Modal'].includes(type)
        ) {
          className = String(className).replace('Active', '.active')
        } else if (_.endsWith(className, 'Disabled') && isAllowState(type, k)) {
          className = String(className).replace('Disabled', ':disabled') + `,.${prefix}--${className}`
        } else if (_.endsWith(className, 'Active') && isAllowState(type, k)) {
          className = String(className).replace('Active', ':active') + `,.${prefix}--${className}`
        } else if (type === 'StarRating' && _.endsWith(className, 'Hover') && isAllowState(type, k)) {
          className = String(className).replace('Hover', ':hover') + `,span.${prefix}--${className}`
        } else if (_.endsWith(className, 'Hover') && isAllowState(type, k)) {
          className = String(className).replace('Hover', ':hover') + `,.${prefix}--${className}`
        } else if (_.endsWith(className, 'Active') && isAllowState(type, k)) {
          className = String(className).replace('Active', ':active') + `,.${prefix}--${className}`
        } else if (_.endsWith(className, 'Hover') && isAllowState(type, k)) {
          className = String(className).replace('Hover', ':hover')
        }
        rootClassBody += '\n;box-sizing: border-box;'
        let postFix = className
        if ((prefix == 'ft--theme--form' || type == 'form') && className == 'label') {
          postFix = `${className}, .${prefix}--${className}.MuiFormLabel-root.Mui-focused`
        } else if ((prefix == 'ft--theme--form' || type == 'form') && className == 'floatLabel') {
          postFix = `label.MuiFormLabel-filled.MuiInputLabel-formControl, .${prefix}--label.MuiFormLabel-root.Mui-focused.MuiInputLabel-formControl`
        }
        if (type === 'Form' && k === 'root') {
          if (styleObject.gridGap) {
            preClass += `.${prefix}--root .ft--theme--Form--wrapper{ grid-gap: ${styleObject.gridGap};}`
          }
        }
        if (type === 'form' && k === 'button') {
          let btnAlign = `.${prefix}--button--align{
            justify-content: ${styleObject.justifyContent || 'flex-start'};
          }`
          preClass += btnAlign
        }
        if (type === 'Stepper' && k === 'icon') {
          const prefBody = getStyle(_.pick(v, ['width', 'widthUnit', 'height', 'heightUnit', 'fontSize']))
          const prefStyle = _.map(prefBody, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`).join(';\n')
          preClass += `.${prefix}--iconRoot{${prefStyle}}`
        }
        if (type === 'Progress' && k === 'root') {
          preClass += `.${prefix}--root .ft--color-primary{ background-color: ${safeLighten(styleObject.color, 0.6)};}`
          preClass += `.${prefix}--root .ft--bar{ background-color: ${styleObject.color};}`
        }
        if (type === 'Typography' && k === 'root') {
          let classBody = _.compact(
            _.map(styleObject, (val, key) => {
              if (FONT_CONST.indexOf(key) !== -1) {
                return `${kebabCase(key)}:${val}${important}`
              }
            })
          ).join(';\n')
          preClass += `.${prefix}--root-to-all * {
            margin: 0;
            ${classBody}
          }`
        }
        if (type === 'Stack' && k === 'root') {
          let stackMaxWidth = stripRootOrigin
          if (String(stripRootOrigin).indexOf('%') > -1) {
            stackMaxWidth = '100%'
          }
          if (styleObject.maxWidth) {
            stackMaxWidth = styleObject.maxWidth
          }
          let colStyle = `.${prefix}--col{
            width: auto !important;
            min-width: ${transformCSSPositionValue(_.get(v, 'width', 0), _.get(v, 'widthUnit', 'px'))};
            max-width: ${stackMaxWidth};
          }`
          let stackMinHeight = transformCSSPositionValue(_.get(v, 'height', 0), _.get(v, 'heightUnit', 'px'))
          preClass += `.${prefix}--row{
            height: auto;
            min-height: ${stackMinHeight};
            max-width: ${stackMaxWidth};
          }
          .${prefix}--row > .stack-element-separator-row{
            max-width: 100%;
            max-width: ${transformCSSPositionValue(_.get(v, 'width', 0), _.get(v, 'widthUnit', 'px'))};
          }${colStyle}`
        }
        if (type === 'galleryCarousel') {
          if (k === 'carouselIcon') {
            const prf = _.trimStart(String(prefix).split('image-gallery-thumbnail').join(''), '.')
            if (v.arrowIconColor) {
              preClass += `.${prf}--root .image-gallery-left-nav svg, .${prf}--root .image-gallery-right-nav svg{color: ${v.arrowIconColor};}`
            }
            if (v.playIconColor) {
              preClass += `.${prf}--root .image-gallery-play-button svg{color: ${v.playIconColor}!important;}`
            }
            if (v.fullScreenIconColor) {
              preClass += `.${prf}--root .image-gallery-fullscreen-button svg{color: ${v.fullScreenIconColor}!important;}`
            }
          }
          if (k === 'original' || k === 'thumbnail' || k === 'imageDescription') {
            if (k === 'imageDescription') {
              postFix = `${postFix} .image-gallery-description`
            } else if (k === 'original') {
              postFix = `${postFix} img.image-gallery-image`
            } else {
              prefix = 'image-gallery-thumbnail.' + prefix
            }
            rootClassBody = _.map(styleObject, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}!important`).join(
              ';\n'
            )
          }
          let classBody = _.map(styleObject, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}!important`).join(
            ';\n'
          )
          if (_.endsWith(k, 'Active')) {
            preClass += `.${prefix}--${String(k).replace('Active', '.active')}{${classBody}}`
          }
        }
        if (k === 'root' && type === 'PowerTable') {
          const sizeStyle = _.pick(styleObject, ['minWidth', 'width', 'maxWidth'])
          let cssStyle = _.map(sizeStyle, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}!important`).join(';\n')
          preClass += `.${prefix}--root .ft--theme--PowerTable--quickButtons.float-panel{${cssStyle}}`
          preClass += `.${prefix}--root .ft--theme--PowerTable--paginationRoot.float-panel{${cssStyle}}`
        }
        if (type === 'FormField' && k === 'root') {
          let stHeight = ''
          let height = parseInt(v.height)
          if (!isNaN(height)) {
            stHeight = 'height:' + transformCSSPositionValue(_.get(v, 'height', 0), _.get(v, 'heightUnit', 'px')) + ';'
          }
          if (v.minHeight) {
            stHeight =
              'min-height:' + transformCSSPositionValue(_.get(v, 'minHeight', 0), _.get(v, 'minHeightUnit', 'px')) + ';'
          }
          if (v.maxHeight) {
            stHeight =
              'max-height:' + transformCSSPositionValue(_.get(v, 'maxHeight', 0), _.get(v, 'maxHeightUnit', 'px')) + ';'
          }
          if (stHeight !== '') {
            preClass += `.${prefix}--root > .ft--control--root, .${prefix}--root > .ft--control--root > .ft--theme--FormField-container,.${prefix}--root > .ft--control--root > .ft--theme--FormField-container .ft--theme--FormField-elRoot{${stHeight}}`
          }
        }
        if (k === 'itemSelected' && type === 'TreeView' && v.backgroundColor) {
          preClass += `.${prefix}--${postFix} > .ftgroup{background-color: ${v.backgroundColor};}`
        }
        if (k === 'itemSelected' && type === 'TreeView' && v.backgroundColor) {
          postFix += `.ft--theme--TreeView--item`
        }
        if (k === 'scroll' || _.endsWith(k, '-scroll') || k === 'scrollbar') {
          const scrollBarColor = _.get(styleObject, 'scrollBarColor', 'auto')
          const scrollBarImage = _.get(styleObject, 'scrollBarImage', '')
          const thumbColor = _.get(styleObject, 'thumbColor', scrollBarColor ? 'ButtonFace' : '')
          const thumbImage = _.get(styleObject, 'thumbImage', '')
          const borderRadius = _.get(styleObject, 'borderRadius', '')
          let borderWidth = _.get(styleObject, 'borderWidth', '')
          let borderColor = _.get(styleObject, 'borderColor', '')
          let borderStyle = _.get(styleObject, 'borderStyle', '')
          let height = _.get(styleObject, 'height')
          let heightUnit = _.get(styleObject, 'heightUnit', 'px')
          if (!height) {
            height = _.get(styleObject, '--scroll-height')
            heightUnit = _.get(styleObject, '--scroll-heightUnit', 'px')
          }
          if (height && height !== 'auto' && height !== 'unset') {
            height = _.endsWith(height, 'px') ? String(height).replace('px', '') : height
            height = transformCSSPositionValue(height, heightUnit, true)
            height = `height:${height};`
          } else {
            height = ''
          }
          const isCustomScroll =
            Boolean(scrollBarColor) ||
            Boolean(scrollBarImage) ||
            Boolean(thumbColor) ||
            Boolean(thumbImage) ||
            Boolean(borderWidth) ||
            Boolean(borderColor) ||
            Boolean(borderStyle) ||
            height ||
            _.get(styleObject, 'width')
          if (isCustomScroll && type === 'FormField') {
            _.set(styleObject, 'width', _.get(styleObject, 'width', '4px'))
          }
          rootClassBody = ''
          if (v.overflowX) {
            rootClassBody += `overflow-x: ${v.overflowX};`
          }
          if (v.overflowY) {
            rootClassBody += `overflow-y: ${v.overflowY};`
          }
          if (isCustomScroll) {
            rootClassBody += `--scrollbar-width: ${_.get(styleObject, 'width')}`
            const classes = []
            classes.push(
              `.${prefix}--${postFix}::-webkit-scrollbar{width: ${_.get(styleObject, 'width', '14px')};${height}background-color: ${scrollBarColor};}`
            )
            if (type === 'PowerTable') {
              classes.push(
                `.${prefix}--thead{padding-${isRTL ? 'left' : 'right'}: ${_.get(styleObject, 'width', '14px')}!important;}`
              )
            }
            classes.push(`.${prefix}--${postFix}::-webkit-scrollbar-track {
              background-color: ${scrollBarColor};
              ${scrollBarImage ? 'background-image:' + scrollBarImage : ''};
              ${Boolean(scrollBarColor) ? 'background-image: none;' : ''}
            }`)

            if (borderWidth || borderStyle || borderColor) {
              if (!borderWidth) {
                borderWidth = '1px'
              }
              if (!borderStyle) {
                borderStyle = 'solid'
              }
              if (!borderColor) {
                borderColor = 'black'
              }
            }

            classes.push(`.${prefix}--${postFix}::-webkit-scrollbar-thumb {
              background-color: ${thumbColor};
              ${thumbImage ? 'background-image:' + thumbImage : ''};
              ${Boolean(thumbColor) ? 'background-image: none;' : ''}
              ${borderRadius ? `border-radius: ${borderRadius};` : ''}
              ${borderWidth && borderColor && borderStyle ? `border: ${borderWidth} ${borderStyle} ${borderColor}  !important;` : ''}
            }`)
            let extraRoot = '' //`.ft--apply--to--child--scroll > div {scrollbar-width: thin;`;
            // if (scrollBarColor && thumbColor) {
            //     extraRoot += `scrollbar-color: ${thumbColor} ${scrollBarColor};`;
            // }
            // extraRoot += '}';
            classes.push(
              `.${prefix}--${postFix}.ft--apply--to--child--scroll > div::-webkit-scrollbar{width: ${_.get(styleObject, 'width', '14px')};${height}}`
            )
            classes.push(`.${prefix}--${postFix}.ft--apply--to--child--scroll > div::-webkit-scrollbar-track {
              background-color: ${scrollBarColor};
              ${scrollBarImage ? 'background-image:' + scrollBarImage : ''};
              ${Boolean(scrollBarColor) ? 'background-image: none;' : ''}
            }`)
            classes.push(`.${prefix}--${postFix}.ft--apply--to--child--scroll > div::-webkit-scrollbar-thumb {
              background-color: ${thumbColor};
              ${thumbImage ? 'background-image:' + thumbImage : ''};
              ${Boolean(thumbColor) ? 'background-image: none;' : ''}
              ${borderRadius ? `border-radius: ${borderRadius};` : ''}
              ${borderWidth && borderColor && borderStyle ? `border: ${borderWidth} ${borderStyle} ${borderColor} !important;` : ''}
            }`)
            preClass += extraRoot + classes.join('\n')
          }
        }
        if (String(k).indexOf('overlay') !== -1) {
          let key = String(k).indexOf('Active') !== -1 ? ':active' : String(k).indexOf('Hover') !== -1 ? ':hover' : ''
          let overlayClass = `position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            content: "";
            pointer-events: none;
            min-height: unset !important;
            ${rootClassBody};
            width: auto;
height: auto;`
          preClass += `.${prefix}--root${key}:after{${overlayClass}}`
          rootClassBody = ''
        }

        if (type === 'eComSlide' && k === 'slide-root') {
          preClass += `.${prefix}--slide-root ~ .ft--nav--slides-ctrl > .category-item-slide{ max-width: ${styleObject.width};}`
        }

        if ((type === 'Scroll' || type === 'Tracker') && (k === 'scroll' || k === 'scrollbar') && v.direction) {
          preClass += `.${prefix}--${k}{direction: ${v.direction};}`
        }

        if (k === 'progress') {
          const styleObj = _.pick(styleObject, [
            'marginLeft',
            'marginRight',
            'marginTop',
            'marginBottom',
            'width',
            'widthUnit',
            'minWidth',
            'minWidthUnit',
            'maxWidth',
            'maxWidthUnit',
            'minHeight',
            'height',
            'maxHeight',
            'minHeightUnit',
            'heightUnit',
            'maxHeightUnit',
          ])
          if (styleObj.widthUnit === '%') {
            styleObj.width = '100'
            styleObj.widthUnit = '%'
          }
          if (_.size(styles) > 0) {
            let str = _.map(styles, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}!important`).join(';')
            preClass += `.${prefix}--root .progress-root{ ${str};height: auto !important;}`
          }
          preClass += `.${prefix}--root .ft--color-primary{ background-color: ${safeLighten(styleObject.color, 0.6)};${styleObject.lineWidth ? 'height: ' + styleObject.lineWidth + 'px;' : ''}}`
          preClass += `.${prefix}--root .ft--bar{ background-color: ${styleObject.color};}`
        }
        if (k === 'progressWrap') {
          const styleObj = _.pick(styleObject, [
            'width',
            'widthUnit',
            'minWidth',
            'minWidthUnit',
            'maxWidth',
            'maxWidthUnit',
            'minHeight',
            'height',
            'maxHeight',
            'minHeightUnit',
            'heightUnit',
            'maxHeightUnit',
          ])
          if (styleObj.widthUnit === '%') {
            styleObj.width = '100'
            styleObj.widthUnit = '%'
          }
          if (_.size(styles) > 0) {
            let str = _.map(styles, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}!important`).join(';')
            preClass += `.${prefix}--progressWrap .progress-root{ ${str};height: auto !important;}`
          }
          preClass += `.${prefix}--progressWrap .ft--color-primary{ background-color: ${safeLighten(styleObject.color, 0.6)};${styleObject.lineWidth ? 'height: ' + styleObject.lineWidth + 'px;' : ''}}`
          preClass += `.${prefix}--progressWrap .ft--bar{ background-color: ${styleObject.color};${styleObject.lineWidth ? 'height: ' + styleObject.lineWidth + 'px;' : ''}}`
          styleObject.color = undefined
        }

        if (userStyle) {
          preClass += `.${prefix}--${postFix}{${userStyle}}`
        }

        if (type === 'StarRating' && (k === 'iconSelected' || k === 'iconHover')) {
          return `span.${prefix}--${postFix}{${rootClassBody}}${preClass}`
        }
        if (type == 'table' && k == 'tra') {
          postFix = 'row:nth-child(even)'
          return `.${prefix}--${postFix}{${rootClassBody}}${preClass}`
        } else if (
          (type === 'Stepper' || type === 'MultiStepper') &&
          ['iconActive', 'iconComplete', 'icon'].includes(k)
        ) {
          return `.${prefix}--${postFix}{${rootClassBody}}${preClass} .${prefix}--iconRoot > .${prefix}--${postFix}{${rootClassBody};color:${v.fill}}`
        } else if (type === 'Badge' && k === 'icon') {
          return `.${prefix}--${postFix}, .${prefix}--${postFix} > svg {${rootClassBody}}${preClass}`
        } else if (type === 'eComCat' && k === 'searchInput') {
          return `.${prefix}--${postFix}, .${prefix}--${postFix}::placeholder{${rootClassBody}}${preClass}`
        } else if (type === 'Link') {
          return `.${prefix}--${postFix}, .${prefix}--${postFix} > a {${rootClassBody}}${preClass}`
        } else {
          return `.${prefix}--${postFix}{${rootClassBody}}${preClass}`
        }
      } else {
        return ''
      }
    }).join('\n')
  }
}

export const formatThemeStyleText = (prefix, theme, mode, stripRoot, isRTL, isPreview) => {
  let types = {}
  let smartTypes = {}
  const defaultStyles = getDefaultStyles()

  if (isPreview) {
    theme.get('lg', Map()).map((v, type) => {
      if (isImmutable(v) && type === 'PageSearch') {
        types[type] = types[type] || ''
        smartTypes[type] = getSmartFieldStyle(v.toJS(), type)
        types[type] += addStyleClasses(`${prefix}--${type}`, v.toJS(), type, false, stripRoot[mode], mode, isRTL)
      } else if (isImmutable(v) && SMART_STYLES_KEYS.indexOf(type) !== -1) {
        const plain = v.toJS()
        const style = _.merge({}, plain.root, plain.messages, plain.input, plain.label, plain)
        if (['SmartVOtpFormField', 'SmartVLoginFormField'].includes(type)) {
          smartTypes[type] = getSmartFieldStyle(style, type)
        } else {
          smartTypes[type] = `${smartTypes[type] || ''}${getSmartFieldStyle(style, `Prj${type}`)}`
        }
      } else if (isImmutable(v) && SMART_STYLES_KEYS.indexOf(type) === -1) {
        types[type] = types[type] || ''
        const styles = _.merge(
          {},
          _.get(defaultStyles, [mode, type], {}),
          v.toJS(),
          type === 'Header'
            ? {
                root: {
                  height: theme.getIn(['lg', type, 'root', 'height'], 64) || 64,
                  ...(mode !== 'lg' ? { minWidth: undefined } : {}),
                },
              }
            : {}
        )
        if (mode != 'lg' && type == 'Page') {
          if (styles && styles.root) {
            delete styles.root.width
          }
        }
        types[type] += addStyleClasses(
          `${prefix}--${type}`,
          styles,
          type === 'Footer' ? 'Strip' : type,
          false,
          stripRoot[mode],
          mode,
          true,
          isPreview,
          isRTL
        )
        if (type === 'PowerTable') {
          let powerFStyle = addStyleClassesForMode(
            `ft--theme--PowerTableField`,
            [_.pick(v.toJS(), ['autoSuggest', 'autoSuggestHover', 'autoSuggestActive', 'autoSuggestSelected'])],
            type,
            false,
            stripRoot[mode],
            mode,
            isRTL,
            isPreview,
            false
          )
          smartTypes[type] = `${smartTypes[type] || ''}${powerFStyle}`
        }
      }
    })
  } else {
    let styles = {}
    theme.get('lg', Map()).map((v, type) => {
      if (isImmutable(v)) {
        styles[type] = styles[type] || []
        styles[type].push(
          _.merge(
            {},
            _.get(defaultStyles, [mode, type], {}),
            type === 'Header'
              ? {
                  root: {
                    height: theme.getIn(['lg', type, 'root', 'height'], 64) || 64,
                  },
                }
              : {},
            v.toJS()
          )
        )
      }
    })
    _.each(styles, (value, type) => {
      types[type] = types[type] || ''
      try {
        if (type === 'PageSearch') {
          types[type] = types[type] || ''
          smartTypes[type] = getSmartFieldStyle(value[0], type)
          types[type] += addStyleClassesForMode(`${prefix}--${type}`, value, type, false, stripRoot[mode], mode, isRTL)
        } else if (SMART_STYLES_KEYS.indexOf(type) !== -1) {
          const plain = value[0]
          const style = _.merge({}, plain.root, plain.messages, plain.input, plain.label, plain)
          if (['SmartVOtpFormField', 'SmartVLoginFormField'].includes(type)) {
            smartTypes[type] = getSmartFieldStyle(style, type)
          } else {
            smartTypes[type] = getSmartFieldStyle(style, `Prj${type}`)
          }
        } else {
          types[type] += addStyleClassesForMode(
            `${prefix}--${type}`,
            value,
            type === 'Footer' ? 'Strip' : type,
            false,
            stripRoot[mode],
            mode,
            isRTL,
            undefined,
            undefined,
            true
          )
          if (type === 'SmartVLoginForm') {
            const plain = value[0]
            const style = _.merge({}, plain.root, plain.messages, plain.input, plain.label, plain)
            smartTypes[type] = getSmartFieldStyle(style, type)
          }
        }
      } catch (error) {
        console.error(error)
      }
    })
  }
  return (
    `.ft--spread--container {
width: 100%;
display: grid;
grid-template-columns: 
    [full-start] minmax(1em, 1fr) 
    [main-start] minmax(0, ${stripRoot[mode]}) [main-end]
    minmax(1em, 1fr) [full-end];
}
.ft--spread--container > .ft--spread--content {
    grid-column: main;
}
.ft--spread--container > .ft--spread--content.ft--spread {
    grid-column: full;
}
.ft--spread--container > .ft--spread--content > .resizer-wrap {
    position: initial;
}
` +
    _.values(types).join('') +
    _.values(smartTypes).join('')
  )
}

export const formatStyleText = (prefix, styleProps, mode, stripRoot, uid, isPreview, isRTL, isCutStrip) => {
  let style = ''

  const type = styleProps.get('type', uid)
  if (isPreview) {
    const { areas } = getGridData(styleProps, mode)
    if (type === 'FormField') {
      style += addStyleClassesForMode(
        `${prefix}--modal`,
        [styleProps.getIn([mode, 'modal'], Map()).toJS()],
        type,
        false,
        stripRoot[mode],
        mode,
        isRTL,
        isPreview,
        isCutStrip
      )
      style += addStyleClassesForMode(
        `${prefix}`,
        [
          _.pick(styleProps.getIn([mode], Map()).toJS(), [
            'autoSuggest',
            'autoSuggestHover',
            'autoSuggestActive',
            'autoSuggestSelected',
            'listAutoComplete',
          ]),
        ],
        type,
        false,
        stripRoot[mode],
        mode,
        isRTL,
        isPreview,
        isCutStrip
      )
    }
    if (type === 'chart') {
      style += addStyleClassesForMode(
        `${prefix}--modal`,
        [styleProps.getIn([mode, 'modal'], Map()).toJS()],
        type,
        false,
        stripRoot[mode],
        mode,
        isRTL,
        isPreview,
        isCutStrip
      )
    }
    style += addStyleClassesForMode(
      `${prefix}`,
      [
        mode === 'lg'
          ? styleProps.get(mode, Map()).toJS()
          : { ...styleProps.get(mode, Map()).toJS(), areas: areas.toJS() },
      ],
      uid === 'Footer' ? 'Strip' : type,
      false,
      stripRoot[mode],
      mode,
      isRTL,
      isPreview,
      isCutStrip
    )
    if (['SmartVLoginForm', 'PowerTable', 'Scheduler', 'TextCounter', 'SmartVOtpForm'].includes(type)) {
      if (styleProps.hasIn([mode, 'mui'])) {
        style += getSmartFieldStyle(styleProps.getIn([mode, 'mui'], Map()).toJS(), `Prj${prefix}`, false, `${prefix}`)
        if (['SmartVOtpForm'].includes(type)) {
          const rawStyle = _.pick(styleProps.getIn([mode, 'mui'], Map()).toJS(), ['invalidIcon', 'validIcon'])
          _.each(rawStyle, (val, key) => {
            if (!_.isEmpty(val)) {
              const cssObj = getStyle(val)
              cssObj.color = cssObj.fill
              const css = _.map(cssObj, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`).join(';\n')
              style += `.${prefix}--root .ft--theme--FormField--${key}{${css}}`
            }
          })
        }
      }
      style += addStyleClassesForMode(
        `ft--theme--Prj${prefix}`,
        [
          _.pick(
            styleProps.getIn([mode], Map()).toJS(),
            _.map(
              ['autoSuggest', 'autoSuggestHover', 'autoSuggestActive', 'autoSuggestSelected', 'listAutoComplete'],
              (key) => `inputFields-${key}`
            )
          ),
        ],
        type,
        false,
        stripRoot[mode],
        mode,
        isRTL,
        isPreview,
        isCutStrip
      )
        .split('inputFields-')
        .join('')
    }
    if (['PowerTable'].includes(type) && styleProps.hasIn([mode, 'modal'])) {
      style += addStyleClassesForMode(
        `${prefix}--modal`,
        [styleProps.getIn([mode, 'modal'], Map()).toJS()],
        type,
        false,
        stripRoot[mode],
        mode,
        isRTL,
        isPreview,
        isCutStrip
      )
    }
    if (
      ['PowerTable', 'TextCounter'].includes(type) &&
      (styleProps.hasIn([mode, 'fmui']) || styleProps.getIn([mode, 'root', 'text'], Map()).size > 0)
    ) {
      let inputStyle = _.assign(
        {},
        { root: styleProps.getIn([mode, 'root', 'text'], Map()).toJS() },
        styleProps.getIn([mode, 'fmui'], Map()).toJS()
      )
      style += getSmartFieldStyle(inputStyle, `Prj${prefix}`, false, `${prefix}`)
    }
    if (
      ['PowerTable'].includes(type) &&
      (styleProps.hasIn([mode, 'fgmui']) || styleProps.getIn([mode, 'root', 'text'], Map()).size > 0)
    ) {
      let inputStyle = _.assign(
        {},
        { root: styleProps.getIn([mode, 'root', 'text'], Map()).toJS() },
        styleProps.getIn([mode, 'fgmui'], Map()).toJS()
      )
      style += getSmartFieldStyle(inputStyle, `GPrj${prefix}`, false, `${prefix}`)
    }
    if (type === 'PageSearch') {
      style += getSmartFieldStyle(styleProps.get(mode, Map()).toJS(), type, true, `${prefix}`)
    }
  } else {
    let styles = []
    styles.push(styleProps.get(mode, Map()).toJS())

    if (type === 'FormField') {
      style += addStyleClassesForMode(
        `${prefix}--modal`,
        [styleProps.getIn([mode, 'modal'], Map()).toJS()],
        type,
        false,
        stripRoot[mode],
        mode,
        isRTL,
        false,
        isCutStrip
      )
    }

    style += addStyleClassesForMode(
      `${prefix}`,
      styles,
      uid === 'Footer' ? 'Strip' : type,
      false,
      stripRoot[mode],
      mode,
      isRTL,
      false,
      isCutStrip
    )

    if (
      ['SmartVLoginForm', 'PowerTable', 'Scheduler', 'TextCounter', 'SmartVOtpForm'].includes(type) &&
      _.hasIn(styles, [0, 'mui'])
    ) {
      style += getSmartFieldStyle(_.get(styles, [0, 'mui'], {}), `Prj${prefix}`, false, `${prefix}`)
    }
    if (
      ['PowerTable', 'TextCounter'].includes(type) &&
      (_.hasIn(styles, [0, 'fmui']) || _.size(_.get(styles, [0, 'root', 'text'], {})))
    ) {
      const textRoot = _.get(styles, [0, 'root', 'text'], {})
      let inputStyle = _.assign({}, { root: textRoot }, _.get(styles, [0, 'fmui'], {}))
      style += getSmartFieldStyle(inputStyle, `Prj${prefix}`, false, `${prefix}`)
    }
    if (
      ['PowerTable', 'TextCounter'].includes(type) &&
      (_.hasIn(styles, [0, 'fgmui']) || _.size(_.get(styles, [0, 'root', 'text'], {})))
    ) {
      const textRoot = _.get(styles, [0, 'root', 'text'], {})
      let inputStyle = _.assign({}, { root: textRoot }, _.get(styles, [0, 'fgmui'], {}))
      style += getSmartFieldStyle(inputStyle, `GPrj${prefix}`, false, `${prefix}`)
    }

    if (type === 'PageSearch') {
      style += getSmartFieldStyle(_.first(styles), type, true, `${prefix}`)
    }
  }

  return style
}
